import {
  CalendarNav,
  Datepicker,
  Eventcalendar,
  formatDate,
  momentTimezone,
  setOptions,
  toast,
} from "@mobiscroll/react";
import {
  OwcBadge,
  OwcIcon,
  OwcIconButton,
  OwcListItem,
  OwcProgressSpinner,
  OwcSelectDropdown,
  OwcTypography,
  OwcButton,
  OwcHyperlink,
  OwcTooltip,
} from "@one/react";
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import "./CalendarTimeline.scss";
import {
  BOOKING_TYPE,
  IconStatus,
  RESOURCE,
  singleEventSkeleton,
  TIMELINE_TYPE,
  TIMELINE_CONFIG,
  TIMELINE_BUFFER_UNITS,
  equipmentStatus
} from "../../../constants";
import moment from "moment-timezone";
import {
  changeDateToUtcString,
  isSameDayBooking,
} from "../../../utils/helpers/text";
import BookingScheduleStatus from "./BookingScheduleStatus";
import { cloneDeep, sortBy, uniqueId } from "lodash";
import { BookingContext, BookingPopUpContext } from "../../booking/context";
import { getAllData } from "../../../utils/helpers/fetching";
import { withApollo } from "react-apollo";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import {
  FIND_BOOKABLE_INSTRUMENTS,
  GET_DIGITAL_LAB_BOOKING_INSTRUMENTS,
  GET_BOOKING_ENTRIES_BY_SITE_AND_INVENTORY_ID,
  LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT,
} from "../../../gql/bookingapi";
import { useGoogleCalendar } from "./hooks/useGoogleCalendar";
import {
  BOOKING_STORAGE_KEYS,
  getPersistedFilter,
  setPersistedFilter,
} from "../../../utils/store/bookingFilterPersistence";
import { eachDayOfInterval } from "date-fns";
import { CustomFilterContext } from "../../booking-instruments/booking-instruments-custom-filter/context";
import {
  getBookingCategory,
  getBookingDisplayText,
} from "../../../components/shared/helpFunctions";
import { formatTitle } from "../../../utils/helpers/googleCalendar";
import { handleOnCellClick } from "../../booking-calendars/helpers";
import TimelineBookingEvent from "./TimelineBookingEvent.tsx";
import RecurringBookingContext from "./RecurringBooking/Context/RecurringBookingContext";
import { RecurringBookingPicker } from "./RecurringBooking/RecurringBookingPicker";
import ConflictPanel from "./RecurringBooking/ConflictPanel";
import {
  buildStandardPageEvents,
  getLatestEventDetailsStartDate,
  getTimelineBoundsFromPageEvent,
  mergeTimelineReservations,
  shouldLoadLatestEventDetails,
  shouldSkipStandardPageLoading,
  shouldSyncDatePickerFromPendingReservations,
} from "./pageLoading.utils";
import {
  buildEquipmentMetaById,
  buildGroupedResources,
  buildLeafResources,
  buildUpdatedPendingReservations,
  canCreateBooking,
  getCalendarCssClass,
  getClickToCreateValue,
  getCreateBookingTitle,
  getCurrentHalfHourSlotUtc,
  getExistingBookingEditErrorMessage,
  getOngoingEventListForDateChange,
  getTimelineUpdateButtonTitle,
  getUnauthorizedEquipmentDetails,
  getUnauthorizedEquipmentMessage,
  hasDatepickerRangeChanged,
  isSingleDayInDisplayTimezone,
  isDatepickerEditRangeChangeInvalid,
  isExistingTimelineBookingEdit,
  normalizeAccessControlDataShape,
  resolveClickToCreateProp,
  restoreOriginalDatepickerSelection,
  shouldSkipTimelineDateChangeOnCancel,
  sortResourcesByAccessControl,
  syncSelectedDatesFromDatepicker,
} from "./customEventCalendar.utils";
import { detectOverlapWithRegularBookings, detectOverlapWithinPending } from "./overlapDetection.utils";
import { setForceInformationOnly } from "../../booking-calendars/redux/actions";
import {
  accessControlStatus,
  extractCidmAccessControlFromConfigItems,
  getBookingAccessControlState,
} from "../../equipment-list/accessControlStatus";
// setup Mobiscroll Moment plugin
momentTimezone.moment = moment;

const dispatchOpenCidmDialog = ({ cidmGroupName, cidmGroupCidmUrl, cidmGroupMoreInfoUrl }) => {
  document.dispatchEvent(new CustomEvent("openCidmDialog", {
    detail: {
      name: cidmGroupName || "",
      url: cidmGroupCidmUrl || "",
      info: cidmGroupMoreInfoUrl || "",
    },
  }));
};

const getTimelineAccessControlReason = ({ accessControlState, cidmGroupName, isBookingAdmin }) => {
  if (accessControlState === accessControlStatus.trainingRequired && cidmGroupName) {
    return `Blocked by CIDM - ${cidmGroupName} is not assigned to user`;
  }

  if (accessControlState === accessControlStatus.restricted) {
    return isBookingAdmin ? null : "Admin access required";
  }

  return null;
};

const GROUP_BY_OPTIONS = [
  { id: "cluster", label: "Cluster ID" },
  { id: "category", label: "Category" },
  { id: "model", label: "Model" },
  { id: "location", label: "Location" },
  { id: "equipment", label: "Equipment" }, // flat
];
const GROUP_BY_CONTROL_ID = "control_groupby";

setOptions({
  theme: "material",
  themeVariant: "light",
});

const VIEW_COLUMNS = {
  [TIMELINE_TYPE.DAY]: 12, // 12 columns for 12 hours visible (8am-8pm typically)
  [TIMELINE_TYPE["3DAY"]]: 12, // 12 columns for 6-hour intervals across 3 days
  [TIMELINE_TYPE.WEEK]: 7, // 7 columns for 7 days
  [TIMELINE_TYPE.MONTH]: 4, // 4 columns for 4 weeks in month view
};

// Minimal width when datepicker with recurring is comfortably fitting into viewport
const datepickerViewportWidthLimit = 1650;

const timelineInfoBarStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "start",
  marginLeft: "4px",
};

const TIMELINE_VISIBLE_RESOURCE_BUFFER = 10;
const TIMELINE_INITIAL_GROUPED_RESOURCE_WINDOW = 30;

const timelineInfoContentStyle = { display: "flex", alignItems: "center" };
const timelineActionsStyle = { marginLeft: "auto", display: "flex", gap: 8 };
const TIMELINE_FETCH_CONTEXT_SETTLE_MS = 250;
const TIMELINE_FETCH_GATE_ENABLED = process.env.NODE_ENV !== "test";

const getNormalizedId = (id) => (id === null || id === undefined ? null : String(id));

const normalizeInventoryId = (item) => {
  if (item === null || item === undefined) { return null; }

  if (typeof item === "object") {
    const objectId = item.inventoryId || item.id;
    return objectId === null || objectId === undefined ? null : String(objectId);
  }

  return String(item);
};

const normalizeInventoryIdList = (inventoryIds = []) => {
  const seenIds = new Set();
  const normalizedIds = [];

  inventoryIds.forEach((item) => {
    const normalizedId = normalizeInventoryId(item);
    if (!normalizedId || seenIds.has(normalizedId)) { return; }
    seenIds.add(normalizedId);
    normalizedIds.push(normalizedId);
  });

  return normalizedIds;
};

const resolveAvailabilityInventoryIds = ({ selectedList, leafResources = [] }) => {
  const normalizedSelectedIds = normalizeInventoryIdList(selectedList);

  if (!normalizedSelectedIds.length) {
    return [];
  }

  const normalizedLeafIds = new Set(
    normalizeInventoryIdList(
      leafResources
        .map((resource) => resource?.id)
        .filter(Boolean),
    ),
  );

  if (!normalizedLeafIds.size) {
    return normalizedSelectedIds;
  }

  const scopedIds = normalizedSelectedIds.filter((inventoryId) =>
    normalizedLeafIds.has(inventoryId),
  );

  // Fallback to selected IDs while resources are still hydrating.
  return scopedIds.length ? scopedIds : normalizedSelectedIds;
};

const mergeUniqueIds = (...idLists) => {
  const mergedIds = [];
  const seenIds = new Set();

  idLists.flat().forEach((id) => {
    if (!id) { return; }
    const normalizedId = String(id);
    if (seenIds.has(normalizedId)) { return; }
    seenIds.add(normalizedId);
    mergedIds.push(id);
  });

  return mergedIds;
};

const getPendingReservationResourceIds = ({ pendingReservations = [], resourceIdSet }) => {
  return pendingReservations
    .map((reservation) => reservation?.resource)
    .filter((id) => resourceIdSet.has(id));
};

const getInitialResourceWindowIds = (orderedIds, windowSize) => {
  return orderedIds.slice(0, Math.min(orderedIds.length, windowSize));
};

const getTimelineLeafResourceIds = (resources = []) => {
  const ids = [];

  resources.forEach((resource) => {
    if (Array.isArray(resource?.children) && resource.children.length > 0) {
      resource.children.forEach((child) => {
        if (child?.id) {ids.push(child.id);}
      });
      return;
    }

    if (resource?.id) { ids.push(resource.id); }
  });

  return ids;
};

const getTimelineRenderedResourceIds = ({
  controlResourceId,
  googleCalendarResources = [],
  isTimelineView,
  isTvMode,
  sortedGroupedResources = [],
}) => {
  const ids = [];

  if (!isTvMode) {
    googleCalendarResources.forEach((resource) => {
      if (resource?.id) ids.push(resource.id);
    });
  }

  if (isTimelineView && !isTvMode) {
    ids.push(controlResourceId);
  }

  sortedGroupedResources.forEach((resource) => {
    if (resource?.id) ids.push(resource.id);

    if (Array.isArray(resource?.children) && resource.children.length > 0) {
      resource.children.forEach((child) => {
        if (child?.id) ids.push(child.id);
      });
    }
  });

  return ids;
};

const pruneMapKeysNotInSet = (map, activeIds) => {
  for (const key of map.keys()) {
    if (!activeIds.has(key)) {
      map.delete(key);
    }
  }
};

const getTimelineVisibleLeafIdsFromDom = ({
  calendarInstance,
  normalizedLeafResourceIdSet,
  normalizedOrderedIdToId,
}) => {
  if (!calendarInstance?._el) return [];

  const resourcesContainer = calendarInstance._el.querySelector(
    ".mbsc-timeline-resources",
  );
  if (!resourcesContainer) return [];

  const containerRect = resourcesContainer.getBoundingClientRect();
  const rowElements = resourcesContainer.querySelectorAll(
    ".mbsc-timeline-resource[data-id], .mbsc-timeline-resource[data-resource-id]",
  );
  const visibleIds = [];

  rowElements.forEach((rowElement) => {
    const rowRect = rowElement.getBoundingClientRect();
    const intersectsViewport =
      rowRect.bottom > containerRect.top && rowRect.top < containerRect.bottom;

    if (!intersectsViewport) return;

    const rowId =
      rowElement.getAttribute("data-id")
      || rowElement.getAttribute("data-resource-id");
    const normalizedRowId = getNormalizedId(rowId);
    if (!normalizedRowId || !normalizedLeafResourceIdSet.has(normalizedRowId)) return;

    visibleIds.push(normalizedOrderedIdToId.get(normalizedRowId) || rowId);
  });

  return mergeUniqueIds(visibleIds);
};

const getTimelineResourceHints = (pageEvent = null) => [
  ...(Array.isArray(pageEvent?.visibleResources) ? pageEvent.visibleResources : []),
  ...(Array.isArray(pageEvent?.resourceIds) ? pageEvent.resourceIds : []),
  ...(Array.isArray(pageEvent?.resources) ? pageEvent.resources : []),
  pageEvent?.firstResource,
  pageEvent?.lastResource,
  pageEvent?.resourceStart,
  pageEvent?.resourceEnd,
];

const createTimelineResourceHintParser = ({
  normalizedLeafResourceIdSet,
  normalizedOrderedIdToId,
  orderedIds,
  renderedResourceIdsForIndexHints,
}) => {
  const normalizeLeafResourceId = (rawId) => {
    const normalizedRawId = getNormalizedId(rawId);
    if (!normalizedRawId || !normalizedLeafResourceIdSet.has(normalizedRawId)) {
      return null;
    }

    return normalizedOrderedIdToId.get(normalizedRawId) || rawId;
  };

  const parseIndexHint = (rawIndex) => {
    if (typeof rawIndex !== "number" || !Number.isFinite(rawIndex)) return null;

    const index = Math.floor(rawIndex);
    return (
      normalizeLeafResourceId(orderedIds[index])
      || normalizeLeafResourceId(renderedResourceIdsForIndexHints[index])
    );
  };

  const parseObjectResourceHint = (resourceHint, fieldName) => {
    const value = resourceHint?.[fieldName];

    switch (typeof value) {
      case "number":
        return parseIndexHint(value);
      case "string":
        return fieldName === "index" ? null : normalizeLeafResourceId(value);
      default:
        return null;
    }
  };

  return (resource) => {
    if (!resource) return null;

    switch (typeof resource) {
      case "number":
        return parseIndexHint(resource);
      case "string":
        return normalizeLeafResourceId(resource);
      case "object":
        return (
          parseObjectResourceHint(resource, "id")
          || parseObjectResourceHint(resource, "resource")
          || parseObjectResourceHint(resource, "index")
        );
      default:
        return null;
    }
  };
};

const resolveTimelineEquipmentScope = ({
  calendarInstance,
  includeInitialGroupedWindow,
  normalizedLeafResourceIdSet,
  orderedIds,
  pageEvent,
  pendingReservations,
  renderedResourceIdsForIndexHints,
  resourceIdSet,
}) => {
  const normalizedOrderedIdToId = new Map(orderedIds.map((id) => [String(id), id]));
  const normalizedOrderedIdToIndex = new Map(
    orderedIds.map((id, index) => [String(id), index]),
  );
  const parseResourceId = createTimelineResourceHintParser({
    normalizedLeafResourceIdSet,
    normalizedOrderedIdToId,
    orderedIds,
    renderedResourceIdsForIndexHints,
  });
  const pendingIds = getPendingReservationResourceIds({
    pendingReservations,
    resourceIdSet,
  });
  const rawVisibleIds = [];

  if (includeInitialGroupedWindow) {
    rawVisibleIds.push(
      ...getInitialResourceWindowIds(
        orderedIds,
        TIMELINE_INITIAL_GROUPED_RESOURCE_WINDOW,
      ),
    );
  }

  rawVisibleIds.push(
    ...getTimelineVisibleLeafIdsFromDom({
      calendarInstance,
      normalizedLeafResourceIdSet,
      normalizedOrderedIdToId,
    }),
  );

  getTimelineResourceHints(pageEvent).forEach((resource) => {
    const id = parseResourceId(resource);
    if (id) rawVisibleIds.push(id);
  });

  const visibleIds = mergeUniqueIds(
    rawVisibleIds.filter((id) => normalizedOrderedIdToId.has(String(id))),
  );

  if (!visibleIds.length) {
    return mergeUniqueIds(
      getInitialResourceWindowIds(
        orderedIds,
        TIMELINE_VISIBLE_RESOURCE_BUFFER * 2 + 1,
      ),
      pendingIds,
    );
  }

  const visibleIndexes = visibleIds
    .map((id) => normalizedOrderedIdToIndex.get(String(id)))
    .filter((index) => index >= 0);

  if (!visibleIndexes.length) {
    return mergeUniqueIds(
      getInitialResourceWindowIds(
        orderedIds,
        TIMELINE_VISIBLE_RESOURCE_BUFFER * 2 + 1,
      ),
      pendingIds,
    );
  }

  const minIndex = Math.min(...visibleIndexes);
  const maxIndex = Math.max(...visibleIndexes);
  const startIndex = Math.max(0, minIndex - TIMELINE_VISIBLE_RESOURCE_BUFFER);
  const endIndex = Math.min(
    orderedIds.length - 1,
    maxIndex + TIMELINE_VISIBLE_RESOURCE_BUFFER,
  );

  return mergeUniqueIds(orderedIds.slice(startIndex, endIndex + 1), pendingIds);
};

const TimelineActionButtons = ({
  createBookingDisabled,
  createBookingTitle,
  handleCreateBookingClick,
  handleCreateFlowCancel,
  handleTimelineEditCancel,
  handleTimelineEditUpdate,
  hasServerError,
  isInformationEdit,
  isTimelineEditMode,
  isUpdatingBooking,
  pendingReservationsLength,
}) => {
  if (isTimelineEditMode) {
    return (
      <div style={timelineActionsStyle}>
        <OwcButton
          variant="secondary"
          onClick={handleTimelineEditCancel}
          disabled={isUpdatingBooking}
        >
          Cancel Changes
        </OwcButton>
        <OwcButton
          variant="primary"
          onClick={handleTimelineEditUpdate}
          disabled={isUpdatingBooking || hasServerError}
          title={getTimelineUpdateButtonTitle({ hasServerError })}
        >
          {isInformationEdit ? "Update Information Booking" : "Update Booking"}
        </OwcButton>
      </div>
    );
  }

  return (
    <div style={timelineActionsStyle}>
      {pendingReservationsLength > 0 && (
        <OwcButton
          variant="secondary"
          onClick={handleCreateFlowCancel}
        >
          Cancel
        </OwcButton>
      )}
      <OwcButton
        variant="primary"
        disabled={createBookingDisabled}
        onClick={handleCreateBookingClick}
        title={createBookingTitle}
      >
        Create Booking
      </OwcButton>
    </div>
  );
};

const TimelineFooter = ({
  createBookingDisabled,
  createBookingTitle,
  handleCreateBookingClick,
  handleCreateFlowCancel,
  handleTimelineEditCancel,
  handleTimelineEditUpdate,
  hasServerError,
  isInformationEdit,
  isLegendVisible,
  isTimelineEditMode,
  isTvMode,
  isTimelineView,
  isUpdatingBooking,
  pendingReservationsLength,
  toggleLegendVisibility,
}) => {
  if (!isTimelineView) {
    return null;
  }

  return (
    <>
      {(isLegendVisible || isTvMode) && <BookingScheduleStatus />}
      {!isTvMode && (
        <div style={timelineInfoBarStyle}>
          <div style={timelineInfoContentStyle}>
            <OwcIcon name="tip" />
            <span style={{ marginLeft: "4px" }}>
              <strong>Quick tip:</strong> Click the timeline to define your
              booking's timeframe
            </span>
          </div>
          <OwcHyperlink
            href="#"
            onClick={toggleLegendVisibility}
            style={{ marginLeft: "4px" }}
          >
            {isLegendVisible ? "Hide legend" : "Show legend"}
          </OwcHyperlink>
          <TimelineActionButtons
            createBookingDisabled={createBookingDisabled}
            createBookingTitle={createBookingTitle}
            handleCreateBookingClick={handleCreateBookingClick}
            handleCreateFlowCancel={handleCreateFlowCancel}
            handleTimelineEditCancel={handleTimelineEditCancel}
            handleTimelineEditUpdate={handleTimelineEditUpdate}
            hasServerError={hasServerError}
            isInformationEdit={isInformationEdit}
            isTimelineEditMode={isTimelineEditMode}
            isUpdatingBooking={isUpdatingBooking}
            pendingReservationsLength={pendingReservationsLength}
          />
        </div>
      )}
    </>
  );
};

export const alignDatePicker = (viewportWidth, isEditMode = false, isDuplicate = false) => {
  if (isEditMode && !isDuplicate) {
    return 'start';
  }

  if (viewportWidth > datepickerViewportWidthLimit) {
    return 'start';
  }

  return 'end';
};

/**
 * Generate a consistent color for a user based on their email/name
 *
 * @param {string} identifier - User email or name
 * @returns {string} - Hex color code
 */
const generateUserColor = (identifier) => {
  if (!identifier) return "#8B9BB3";

  // Predefined darker pastel color palette for avatars (better contrast with white text)
  const colors = [
    "#E89CAE",
    "#E6C18B",
    "#E6E68B",
    "#9FE69F",
    "#8BC5E6",
    "#A8B5D1",
    "#E68BB5",
    "#B695E6",
    "#8BB5E6",
    "#E6C78B",
    "#B5E68B",
    "#8BE6C7",
    "#E6A18B",
    "#C78BE6",
    "#8BA1E6",
    "#E6B58B",
    "#A1C7A1",
    "#E6C195",
    "#C7A1C7",
    "#95D185",
    "#D1A1A8",
    "#A1C1E6",
    "#D1B385",
    "#B5A1D1",
    "#9FD19F",
  ];

  // Simple hash function to get consistent color
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

const normalizeAvailabilityValue = (availability) => {
  if (availability === null || availability === undefined) {
    return "";
  }

  return String(availability).trim();
};

const getAvailabilityLabelText = ({ availability, selectedCount }) => {
  const normalizedAvailability = normalizeAvailabilityValue(availability);
  const normalizedAvailabilityUpper = normalizedAvailability.toUpperCase();

  if (normalizedAvailabilityUpper === "NONE") {
    return null;
  }

  if (normalizedAvailabilityUpper === "ALL") {
    if (selectedCount > 0) {
      return `${selectedCount} of ${selectedCount}`;
    }

    return "ALL";
  }

  return normalizedAvailability;
};

const isFullyAvailableDate = (normalizedAvailabilityUpper) => {
  // Backend can return values like "1 of 1" for days that are not fully
  // available for 24h. Only an explicit "ALL" should be treated as full-day
  // availability for green coloring in the datepicker.
  return normalizedAvailabilityUpper === "ALL";
};

const DATEPICKER_FULL_AVAILABILITY_COLORS = {
  background: "#E6F4EA",
  textColor: "#1E8E3E",
};

const DATEPICKER_PARTIAL_AVAILABILITY_TEXT_COLOR = "#ED4A0D";

const getDatepickerMinValue = ({
  isEditMode,
  isOngoing,
  selectedStartDate,
  siteTimezone,
}) => {
  if (isEditMode && isOngoing) {
    return selectedStartDate;
  }

  return moment.tz(siteTimezone).startOf("hour").format();
};

const getCalendarSelectedDate = ({ timelineSelectedDate, siteTimezone, isTvMode }) => {
  const baseDate = timelineSelectedDate ?? moment().tz(siteTimezone).toDate();
  if (isTvMode) {
    return moment(baseDate).startOf("day").toDate();
  }

  return baseDate;
};

const getCalendarRefDate = ({ timelineSelectedDate, siteTimezone, isTvMode, view }) => {
  const currentDate = timelineSelectedDate ?? moment().tz(siteTimezone).toDate();

  if (isTvMode) {
    return moment(currentDate).startOf("day").toDate();
  }

  switch (view) {
    case "day":
      return moment(currentDate)
        .clone()
        .subtract(TIMELINE_BUFFER_UNITS.DAY + 1, "days")
        .toDate();
    case "week":
      return moment(currentDate)
        .clone()
        .subtract(TIMELINE_BUFFER_UNITS.WEEK, "days")
        .toDate();
    case "3day":
      return moment(currentDate)
        .clone()
        .subtract(TIMELINE_BUFFER_UNITS["3DAY"], "days")
        .toDate();
    case "month":
      return moment(currentDate)
        .clone()
        .subtract(TIMELINE_BUFFER_UNITS.MONTH, "months")
        .startOf("month")
        .toDate();
    default:
      return moment(currentDate)
        .clone()
        .subtract(TIMELINE_BUFFER_UNITS.DAY, "days")
        .toDate();
  }
};

const canEditExistingBooking = (event, user) => {
  const isBookingAdmin = !!user?.roles?.some((role) => role === "BookingAdmin");
  const isCreator = Boolean(event?.createdBy && user?.user && event.createdBy === user.user);
  const isBookedFor = Boolean(
    Array.isArray(event?.reservedForEmails)
      && user?.email
      && event.reservedForEmails.includes(user.email),
  );

  return isBookingAdmin || isCreator || isBookedFor;
};

const buildAvailableEventRow = (normalizedEvent, eventId) => {
  let eventRow = {
    ...singleEventSkeleton,
    start: changeDateToUtcString(normalizedEvent.start),
    end: changeDateToUtcString(normalizedEvent.end),
    resource: normalizedEvent.resource,
    id: eventId,
    // Mobiscroll otherwise blocks drag/resize internally (silent revert) when
    // `overlap: false` is set on the dragged event. We rely on our own
    // `hasOverlap` validation inside `onEventUpdate` to enforce overlap rules
    // (admin bypass in timeline view, toast for non-admins) instead of
    // Mobiscroll's silent block.
    overlap: true,
  };

  const availableRow = IconStatus.find(
    (statusItem) => statusItem.text === BOOKING_TYPE.AVAILABLE,
  );

  if (!availableRow) {
    return eventRow;
  }

  eventRow = {
    ...eventRow,
    type: BOOKING_TYPE.AVAILABLE,
    color: availableRow.backgroundColor,
    fcolor: availableRow.color,
    title: BOOKING_TYPE.AVAILABLE,
  };

  return eventRow;
};

const buildInformationFirstEventOrder = (forceInformationOnlyRef) => (a, b) => {
  // Pending slots get visually demoted to the same height/position as real
  // Information bookings when the wizard is forced into Information-only mode
  // (overlap detected). To keep this reactive WITHOUT recomputing the event
  // data array (which would make Mobiscroll snap mid-drag slots back to their
  // previous position), the comparator reads the latest flag from a ref.
  const force = forceInformationOnlyRef?.current === true;
  const aIsPending = typeof a?.id === "string" && a.id.startsWith("available_");
  const bIsPending = typeof b?.id === "string" && b.id.startsWith("available_");
  const aInfo = (a?.isInformationBooking || (force && aIsPending)) ? 0 : 1;
  const bInfo = (b?.isInformationBooking || (force && bIsPending)) ? 0 : 1;
  if (aInfo !== bInfo) return aInfo - bInfo;
  const aStart = a?.start ? new Date(a.start).getTime() : 0;
  const bStart = b?.start ? new Date(b.start).getTime() : 0;
  if (aStart !== bStart) return aStart - bStart;
  const aTitle = (a?.title || "").toString();
  const bTitle = (b?.title || "").toString();
  return aTitle.localeCompare(bTitle);
};

const CustomEventCalendar = ({
  eventData,
  resourceData,
  visibleDatePicker = true,
  selectedStartDate,
  setSelectedStartDate,
  selectedEndDate,
  setSelectedEndDate,
  dataTimeZone,
  displayTimeZone,
  clearPendingReservation,
  updatePendingReservation,
  removeEquipmentAndReservation,
  pendingReservations,
  getReservationBooking,
  getEventDetails,
  setloading: setLoading,
  setEventsData,
  loading,
  view,
  setView,
  calView,
  setCalView,
  manipulatePopoverContent,
  siteTimezone,
  newEquipmentIds,
  setNewEquipmentIds,
  getLatestEventDetailsOnly,
  setCancelStatus,
  setDeleteResourceId,
  client,
  isTimelineView,
  user = null,
  renderCustomEquipmentEvent = null,
  originalSelectedStartDate = null,
  originalSelectedEndDate = null,
  onPermissionCheckersReady = null,
  isGroupView = false,
  handleCreateBookingFromEquipmentView = null,
  bookingFetchError = false,
  timelineGroupByDefault = "category",
  timelineGroupByStorageKey = BOOKING_STORAGE_KEYS.TIMELINE_GROUP_BY_VIEW,
  timelineStorageOptions = {},
  onTimelineUpdateBooking = null,
  setPopupEnableStatus = null,
  onCancelCreateFlow = null,
  isBookingWizard = false,
}) => {
  const { isEditMode, isDuplicate, editBookingData, isOngoing } =
    useContext(BookingContext);
  const { dispatchAction: bookingPopUpDispatchAction } =
    useContext(BookingPopUpContext);
  const { selectedList } = useContext(CustomFilterContext);
  const dispatch = useDispatch();
  const calendarRef = useRef(null);
  const [timelineSelectedDate, setTimelineSelectedDate] = useState(null);
  // When we set timelineSelectedDate programmatically (e.g. from
  // editBookingData.dateFrom on entering Full Edit), Mobiscroll 5.35 fires
  // onSelectedDateChange with a `_viewDate` snapped to the visible page
  // origin -- which, combined with our large refDate buffer (90 days back in
  // WEEK view), lands on a different day than what we asked for. That
  // overrides our programmatic date and shifts the timeline (e.g. June 5 ->
  // June 8). We ignore the first such callback after each programmatic set.
  const suppressMobiscrollDateOverrideRef = useRef(false);
  const [eventHover, setEventHover] = useState(null);
  const [rangeInst, setRangeInst] = useState();
  const [colorsData, setColorsData] = useState([]);
  const [labelsData, setLabelsData] = useState([]);
  const [disableDateList, setDisableDateList] = useState([]);
  const [calendarInstance, setCalendarInstance] = useState(null);
  const [isLegendVisible, setIsLegendVisible] = useState(false);
  const [equipmentPermissions, setEquipmentPermissions] = useState(new Map());
  const [equipmentBookingCapabilities, setEquipmentBookingCapabilities] =
    useState(new Map());
  const equipmentPermissionCacheRef = useRef(new Map());
  const equipmentPermissionPendingRef = useRef(new Map());
  const loadingEquipmentRequestCountRef = useRef(new Map());
  const [loadingEquipmentIds, setLoadingEquipmentIds] = useState(() => new Set());
  const usersList = useSelector((state) => state.users.users);
  const {
    isRecurringBooking,
    temporaryStartDate,
    temporaryEndDate,
    temporarySelectedDates,
    setTemporaryStartDate,
    setTemporaryEndDate,
    debouncedSetTempDates,
    conflicts,
    setEventDataContext,
    setRecoursesContext,
    getTrimmedRRuleString,
    resetAll
  } = useContext(RecurringBookingContext);
  const isTvMode = useSelector((state) => state?.bookingInstruments?.isTvMode);
  // When the admin drags a new pending slot that overlaps a real booking or
  // another pending slot, CalendarMainPage dispatches this flag so the whole
  // wizard switches to Information-only mode. We use the same signal to
  // visually demote the pending slot (18px height, top of the row) — making
  // it obvious to the admin that they can only save it as an Information slot.
  const forceInformationOnly = useSelector(
    (state) => !!state?.reservations?.forceInformationOnly,
  );
  // Keep the flag in a ref so the eventOrder comparator (passed to Mobiscroll
  // as a stable function reference) can read the latest value without us
  // having to swap the comparator identity -- swapping it would force
  // Mobiscroll to re-render the event grid and snap mid-drag pending slots
  // back to their previous position.
  const forceInformationOnlyRef = useRef(forceInformationOnly);
  useEffect(() => {
    forceInformationOnlyRef.current = forceInformationOnly;
  }, [forceInformationOnly]);
  const informationFirstEventOrder = useMemo(
    () => buildInformationFirstEventOrder(forceInformationOnlyRef),
    [],
  );
  const [isEquipmentHdrOpen, setEquipmentHdrOpen] = useState(false);
  const [localFetchError, setLocalFetchError] = useState(false);
  const hasServerError = bookingFetchError || localFetchError;
  const [groupBy, setGroupBy] = useState(() => {
    return getPersistedFilter(
      timelineGroupByStorageKey,
      timelineGroupByDefault,
      timelineStorageOptions,
    );
  });
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const isDatepickerOpen = rangeInst?._isOpen || false;
  const isDatepickerCancelledRef = useRef(false);
  const dpCloseReasonRef = useRef(null);
  const dateChangeInProgressRef = useRef(false);
  const slotUpdateInProgressRef = useRef(false);
  const lastTimelineBoundsRef = useRef(null);
  const lastVisibleScopeKeyRef = useRef("");
  const lastCapabilityScopeKeyRef = useRef("");
  const equipmentAvailabilityCacheRef = useRef(new Map());
  const reservationRowsFetchedByRangeRef = useRef(new Map());
  const reservationRowsInFlightByRangeRef = useRef(new Map());
  const datepickerAvailabilityRequestRef = useRef(0);
  const timelineFetchGateReadyRef = useRef(true);
  const timelineFetchGateTimerRef = useRef(null);
  const pendingTimelinePageEventRef = useRef(null);
  const pendingTimelineVirtualArgsRef = useRef(null);
  const virtualLoadingDebounceRef = useRef(null);
  const virtualLoadingRequestIdRef = useRef(0);
  const lastColumnWidthCssRef = useRef("");
  const resourceCatchUpDoneRef = useRef(false);



  const [timelineEditDraft, setTimelineEditDraft] = useState(null);
  const isTimelineEditMode = !!timelineEditDraft && isTimelineView;
  const [isUpdatingBooking, setIsUpdatingBooking] = useState(false);


  useEffect(() => {
    if (!setPopupEnableStatus) return;
    const hasUnsaved =
      isTimelineEditMode || (pendingReservations && pendingReservations.length > 0);
    setPopupEnableStatus(!!hasUnsaved);
  }, [isTimelineEditMode, pendingReservations, setPopupEnableStatus]);

  useEffect(() => {
    const checkViewport = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  useEffect(() => {
    setTemporaryStartDate(originalSelectedStartDate);
    setTemporaryEndDate(originalSelectedEndDate);
  }, [originalSelectedEndDate, originalSelectedStartDate, setTemporaryStartDate, setTemporaryEndDate])

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (isTvMode) root.classList.add("no-scroll");
    else root.classList.remove("no-scroll");
    return () => root.classList.remove("no-scroll");
  }, [isTvMode]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        setPersistedFilter(
          timelineGroupByStorageKey,
          groupBy,
          timelineStorageOptions,
        );
      } catch (err) {
        console.warn("Failed to persist TIMELINE_GROUP_BY_VIEW:", err);
      }
    }, 100);
    return () => clearTimeout(timeoutId); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, timelineGroupByStorageKey, timelineStorageOptions]);

  const equipmentMetaById = useMemo(() => {
    return buildEquipmentMetaById({ eventData, isTimelineView, resourceData });
  }, [eventData, resourceData, isTimelineView]);

  const leafResources = useMemo(() => {
    return buildLeafResources(equipmentMetaById);
  }, [equipmentMetaById]);

  const availabilityInventoryIds = useMemo(() => {
    return resolveAvailabilityInventoryIds({ selectedList, leafResources });
  }, [leafResources, selectedList]);

  const groupedResources = useMemo(() => {
    return buildGroupedResources({ equipmentMetaById, groupBy, leafResources });
  }, [leafResources, equipmentMetaById, groupBy]);

  // Google Calendar integration
  const {
    isLoggedIn: isGoogleLoggedIn,
    selectedCalendarIds,
    googleEvents,
    loadCalendarEvents,
    calendarData,
    toggleCalendar,
  } = useGoogleCalendar();
  const [googleCalendarResources, setGoogleCalendarResources] = useState([]);
  const accessConfigEnrichmentCacheRef = useRef(new Map());
  const accessConfigEnrichmentPendingRef = useRef(new Map());
  const [accessConfigCacheVersion, setAccessConfigCacheVersion] = useState(0);
  const accessConfigBatchTimerRef = useRef(null);

  const bumpAccessConfigVersion = useCallback(() => {
    if (accessConfigBatchTimerRef.current) {
      return;
    }
    accessConfigBatchTimerRef.current = setTimeout(() => {
      accessConfigBatchTimerRef.current = null;
      setAccessConfigCacheVersion((current) => current + 1);
    }, 200);
  }, []);

  useEffect(() => {
    return () => {
      if (accessConfigBatchTimerRef.current) {
        clearTimeout(accessConfigBatchTimerRef.current);
      }
    };
  }, []);

  const getAccessConfigEnrichment = useCallback(
    async (inventoryId) => {
      if (!inventoryId) {
        return {};
      }

      if (accessConfigEnrichmentCacheRef.current.has(inventoryId)) {
        return accessConfigEnrichmentCacheRef.current.get(inventoryId) || {};
      }

      if (accessConfigEnrichmentPendingRef.current.has(inventoryId)) {
        return accessConfigEnrichmentPendingRef.current.get(inventoryId);
      }

      const pendingRequest = client
        .query({
          query: LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT,
          variables: {
            limit: 20,
            modelName: "BOOKINGACCESSCONFIG",
            inventoryId: { eq: inventoryId },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          const items =
            response?.data?.listBookingAccessConfigurationByEquipment?.items ||
            [];
          const enrichment = extractCidmAccessControlFromConfigItems(items);
          accessConfigEnrichmentCacheRef.current.set(inventoryId, enrichment);
          bumpAccessConfigVersion();
          return enrichment;
        })
        .catch((error) => {
          console.error("Error loading booking access config:", error);
          accessConfigEnrichmentCacheRef.current.set(inventoryId, {});
          bumpAccessConfigVersion();
          return {};
        })
        .finally(() => {
          accessConfigEnrichmentPendingRef.current.delete(inventoryId);
        });

      accessConfigEnrichmentPendingRef.current.set(inventoryId, pendingRequest);
      return pendingRequest;
    },
    [client, bumpAccessConfigVersion],
  );

  const getCachedAccessConfigEnrichment = useCallback((inventoryId) => {
    if (!inventoryId) {
      return {};
    }

    return accessConfigEnrichmentCacheRef.current.get(inventoryId) || {};
  }, []);

  const sortedGroupedResources = useMemo(() => {
    const cacheVersion = accessConfigCacheVersion;
    return sortResourcesByAccessControl({
      resources: groupedResources,
      equipmentMetaById,
      getCachedAccessConfigEnrichment,
      user,
      isTimelineView,
      cacheVersion,
    });
  }, [
    equipmentMetaById,
    getCachedAccessConfigEnrichment,
    groupedResources,
    isTimelineView,
    user,
    accessConfigCacheVersion,
  ]);

  const sortedTimelineLeafResourceIds = useMemo(() => {
    return getTimelineLeafResourceIds(sortedGroupedResources);
  }, [sortedGroupedResources]);

  const sortedTimelineLeafResourceIdSet = useMemo(
    () => new Set(sortedTimelineLeafResourceIds),
    [sortedTimelineLeafResourceIds],
  );

  const normalizedTimelineLeafResourceIdSet = useMemo(
    () => new Set(sortedTimelineLeafResourceIds.map((id) => String(id))),
    [sortedTimelineLeafResourceIds],
  );

  const timelineRenderedResourceIdsForIndexHints = useMemo(() => {
    return getTimelineRenderedResourceIds({
      controlResourceId: GROUP_BY_CONTROL_ID,
      googleCalendarResources,
      isTimelineView,
      isTvMode,
      sortedGroupedResources,
    });
  }, [googleCalendarResources, isTimelineView, isTvMode, sortedGroupedResources]);

  const syncLoadingEquipmentIds = useCallback(() => {
    const nextIds = Array.from(loadingEquipmentRequestCountRef.current.entries())
      .filter(([, count]) => count > 0)
      .map(([id]) => id);
    setLoadingEquipmentIds(new Set(nextIds));
  }, []);

  const markEquipmentRowsLoading = useCallback(
    (equipmentIds = []) => {
      equipmentIds.filter(Boolean).forEach((id) => {
        const normalizedId = String(id);
        const previousCount =
          loadingEquipmentRequestCountRef.current.get(normalizedId) || 0;
        loadingEquipmentRequestCountRef.current.set(normalizedId, previousCount + 1);
      });
      syncLoadingEquipmentIds();
    },
    [syncLoadingEquipmentIds],
  );

  const unmarkEquipmentRowsLoading = useCallback(
    (equipmentIds = []) => {
      equipmentIds.filter(Boolean).forEach((id) => {
        const normalizedId = String(id);
        const previousCount =
          loadingEquipmentRequestCountRef.current.get(normalizedId) || 0;
        const nextCount = Math.max(0, previousCount - 1);
        if (nextCount === 0) {
          loadingEquipmentRequestCountRef.current.delete(normalizedId);
          return;
        }
        loadingEquipmentRequestCountRef.current.set(normalizedId, nextCount);
      });
      syncLoadingEquipmentIds();
    },
    [syncLoadingEquipmentIds],
  );

  const resolveTimelineEquipmentIdsForFetch = useCallback(
    (pageEvent = null) => {
      const orderedIds = sortedTimelineLeafResourceIds;

      if (!orderedIds.length) {
        return [];
      }

      return resolveTimelineEquipmentScope({
        calendarInstance,
        includeInitialGroupedWindow:
          isTimelineView
          && groupBy?.toUpperCase?.() !== "EQUIPMENT"
          && !lastVisibleScopeKeyRef.current,
        normalizedLeafResourceIdSet: normalizedTimelineLeafResourceIdSet,
        orderedIds,
        pageEvent,
        pendingReservations,
        renderedResourceIdsForIndexHints: timelineRenderedResourceIdsForIndexHints,
        resourceIdSet: sortedTimelineLeafResourceIdSet,
      });
    },
    [
      calendarInstance,
      groupBy,
      isTimelineView,
      timelineRenderedResourceIdsForIndexHints,
      normalizedTimelineLeafResourceIdSet,
      pendingReservations,
      sortedTimelineLeafResourceIds,
      sortedTimelineLeafResourceIdSet,
    ],
  );

  const resolveFirstGroupedTimelineScopeFromRows = useCallback(
    async (pageEvent = null) => {
      const orderedIds = sortedTimelineLeafResourceIds;
      if (!orderedIds.length) {
        return [];
      }

      const normalizedOrderedIdToId = new Map(
        orderedIds.map((id) => [String(id), id]),
      );
      const domCheckRetryDelaysMs = [0, 80, 180, 320];

      for (const delayMs of domCheckRetryDelaysMs) {
        if (delayMs > 0) {
          await new Promise((resolve) => {
            window.setTimeout(resolve, delayMs);
          });
        }

        const visibleLeafIds = getTimelineVisibleLeafIdsFromDom({
          calendarInstance,
          normalizedLeafResourceIdSet: normalizedTimelineLeafResourceIdSet,
          normalizedOrderedIdToId,
        });


        if (visibleLeafIds.length > 0) {
          const result = resolveTimelineEquipmentScope({
            calendarInstance,
            includeInitialGroupedWindow: false,
            normalizedLeafResourceIdSet: normalizedTimelineLeafResourceIdSet,
            orderedIds,
            pageEvent,
            pendingReservations,
            renderedResourceIdsForIndexHints: timelineRenderedResourceIdsForIndexHints,
            resourceIdSet: sortedTimelineLeafResourceIdSet,
          });
          return result;
        }
      }

      const result = resolveTimelineEquipmentScope({
        calendarInstance,
        includeInitialGroupedWindow: true,
        normalizedLeafResourceIdSet: normalizedTimelineLeafResourceIdSet,
        orderedIds,
        pageEvent,
        pendingReservations,
        renderedResourceIdsForIndexHints: timelineRenderedResourceIdsForIndexHints,
        resourceIdSet: sortedTimelineLeafResourceIdSet,
      });
      return result;
    },
    [
      calendarInstance,
      normalizedTimelineLeafResourceIdSet,
      pendingReservations,
      sortedTimelineLeafResourceIds,
      sortedTimelineLeafResourceIdSet,
      timelineRenderedResourceIdsForIndexHints,
    ],
  );

  useEffect(() => {
    if (!isTimelineView || !leafResources.length) {
      return;
    }

    const scopedEquipmentIds = resolveTimelineEquipmentIdsForFetch();
    scopedEquipmentIds.forEach((inventoryId) => {
      getAccessConfigEnrichment(inventoryId).catch(() => {});
    });
  }, [
    getAccessConfigEnrichment,
    isTimelineView,
    leafResources,
    resolveTimelineEquipmentIdsForFetch,
  ]);

  useEffect(() => {
    const activeInventoryIds = new Set(leafResources.map((resource) => resource.id));

    [
      accessConfigEnrichmentCacheRef.current,
      accessConfigEnrichmentPendingRef.current,
      equipmentPermissionCacheRef.current,
      equipmentPermissionPendingRef.current,
    ].forEach((map) => pruneMapKeysNotInSet(map, activeInventoryIds));
  }, [leafResources]);

  /**
   * Check if user has permission to book a specific equipment Uses the same
   * logic as EquipmentMainPage checkbox function:
   *
   * - For booking controlled equipment: checks user.id and user.user in
   *   bookableForUsers array
   * - If controlled and user not in list: allows BookingAdmin role access
   * - For non-controlled equipment: allows all users
   *
   * @param {string} inventoryId - Equipment inventory ID
   * @returns {object} - Permission status and reason if denied
   */
  const checkEquipmentPermission = useCallback(
    async (inventoryId) => {
      if (!inventoryId) {
        return { canBook: false, reason: "Equipment not found" };
      }

      const cachedPermission = equipmentPermissionCacheRef.current.get(inventoryId);
      if (cachedPermission) {
        return cachedPermission;
      }

      const pendingPermission = equipmentPermissionPendingRef.current.get(inventoryId);
      if (pendingPermission) {
        return pendingPermission;
      }

      const cachePermission = (permission) => {
        equipmentPermissionCacheRef.current.set(inventoryId, permission);
        return permission;
      };

      const permissionPromise = (async () => {
      try {
        // First check equipment booking capabilities
        const equipmentDetails = await client.query({
          query: GET_DIGITAL_LAB_BOOKING_INSTRUMENTS,
          variables: { inventoryId },
          fetchPolicy: "no-cache",
        });
        const equipment = equipmentDetails.data?.getDigitalLabBookingInstrument;

        if (!equipment) {
          return cachePermission({ canBook: false, reason: "Equipment not found" });
        }

        if (equipment.status === equipmentStatus.deleted.key) {
          return cachePermission({ canBook: false, reason: "Equipment not found" });
        }

        if (!equipment.isBookable) {
          return cachePermission({
            canBook: false,
            reason: "Equipment is not bookable",
          });
        }
        const accessConfigEnrichment =
          await getAccessConfigEnrichment(inventoryId);
        const accessControlData = normalizeAccessControlDataShape({
          ...(equipmentMetaById.get(inventoryId) || {}),
          ...equipment.addInfo,
          ...accessConfigEnrichment,
          inventoryId,
        });
        const { status: accessControlState, cidmGroupName } =
          getBookingAccessControlState(accessControlData, user);
        const isBookingAdmin = user?.roles?.some(
          (role) => role === "BookingAdmin",
        );

        if (accessControlState === accessControlStatus.allowed) {
          return cachePermission({
            canBook: true,
            reason: null,
            accessControlState,
            cidmGroupName,
            isAdminAccess: false,
          });
        }

        if (accessControlState === accessControlStatus.trainingRequired) {
          return cachePermission({
            canBook: false,
            reason: getTimelineAccessControlReason({
              accessControlState,
              cidmGroupName,
              isBookingAdmin,
            }),
            accessControlState,
            cidmGroupName,
            isAdminAccess: false,
          });
        }

        return cachePermission({
          canBook: !!isBookingAdmin,
          reason: getTimelineAccessControlReason({
            accessControlState,
            cidmGroupName,
            isBookingAdmin,
          }),
          accessControlState,
          cidmGroupName,
          isAdminAccess: !!isBookingAdmin,
        });
      } catch (error) {
        console.error("Error checking equipment permission:", error);
        return { canBook: false, reason: "Error checking permissions" };
      }
      })();

      equipmentPermissionPendingRef.current.set(inventoryId, permissionPromise);
      try {
        return await permissionPromise;
      } finally {
        equipmentPermissionPendingRef.current.delete(inventoryId);
      }
    },
    [
      client,
      equipmentMetaById,
      getAccessConfigEnrichment,
      user,
    ],
  );

  /**
   * Check equipment booking slots availability
   *
   * @param {string} inventoryId - Equipment inventory ID
   * @param {string} startDate - Start date for checking
   * @param {string} endDate - End date for checking
   * @returns {object} - Availability status and existing bookings
   */
  const checkEquipmentAvailability = useCallback(
    async (inventoryId, startDate, endDate) => {
      try {
        const bookingEntries = await client.query({
          query: GET_BOOKING_ENTRIES_BY_SITE_AND_INVENTORY_ID,
          variables: {
            site: user?.site,
            inventoryId: { eq: inventoryId },
            filter: {
              start: { between: [startDate, endDate] },
            },
            limit: 1000,
          },
          fetchPolicy: "no-cache",
        });

        if (bookingEntries?.errors && bookingEntries.errors.length > 0) {
          setLocalFetchError(true);
          snackbarService.show({
            message: "Unexpected Server Error",
            type: "error",
          });
          return {
            hasConflicts: false,
            existingBookings: [],
            availableSlots: [],
            hasError: true,
          };
        }

        const entries =
          bookingEntries.data?.bookingEntriesBySiteAndInventoryId?.items || [];
        const activeBookings = entries.filter((entry) => entry.active);

        return {
          hasConflicts: activeBookings.length > 0,
          existingBookings: activeBookings,
          availableSlots: entries.filter(
            (entry) => entry.type === BOOKING_TYPE.AVAILABLE,
          ),
          hasError: false,
        };
      } catch (error) {
        console.error("Error checking equipment availability:", error);
        setLocalFetchError(true);
        snackbarService.show({
          message: "Unexpected Server Error",
          type: "error",
        });
        return {
          hasConflicts: false,
          existingBookings: [],
          availableSlots: [],
          hasError: true,
        };
      }
    },
    [client, user],
  );

  /** Batch check permissions and capabilities for all equipment */
  const checkAllEquipmentPermissions = useCallback(async (equipmentIdsOverride = null) => {
    if (!leafResources.length || !user) return;

    const scopedEquipmentIds = Array.isArray(equipmentIdsOverride)
      ? equipmentIdsOverride.filter(Boolean)
      : leafResources.map((resource) => resource?.id).filter(Boolean);

    if (!scopedEquipmentIds.length) {
      return;
    }

    const permissionsMap = new Map();
    const capabilitiesMap = new Map();

    await Promise.all(
      scopedEquipmentIds.map(async (inventoryId) => {
        if (!inventoryId) return;

        try {
          const permission = await checkEquipmentPermission(inventoryId);
          permissionsMap.set(inventoryId, permission);
          capabilitiesMap.set(inventoryId, {
            canCreate: permission.canBook,
            canView: true, // Generally all equipment can be viewed
            reason: permission.reason,
          });
        } catch (error) {
          permissionsMap.set(inventoryId, {
            canBook: false,
            reason: "Permission check failed",
          });
          capabilitiesMap.set(inventoryId, {
            canCreate: false,
            canView: true,
            reason: "Permission check failed",
          });
        }
      }),
    );

    setEquipmentPermissions((prevPermissions) => {
      const nextPermissions = new Map(prevPermissions);
      permissionsMap.forEach((permission, inventoryId) => {
        nextPermissions.set(inventoryId, permission);
      });
      return nextPermissions;
    });

    setEquipmentBookingCapabilities((prevCapabilities) => {
      const nextCapabilities = new Map(prevCapabilities);
      capabilitiesMap.forEach((capability, inventoryId) => {
        const currentCapability = nextCapabilities.get(inventoryId) || {};
        nextCapabilities.set(inventoryId, {
          ...currentCapability,
          ...capability,
        });
      });
      return nextCapabilities;
    });
  }, [leafResources, user, checkEquipmentPermission]);

  /**
   * Get permission status for a specific equipment
   *
   * @param {string} inventoryId - Equipment inventory ID
   * @returns {object} - Permission and capability status
   */
  const getEquipmentBookingStatus = useCallback(
    (inventoryId) => {
      const permissions = equipmentPermissions.get(inventoryId);
      const capabilities = equipmentBookingCapabilities.get(inventoryId);

      return {
        canBook: permissions?.canBook && capabilities?.canCreate,
        reason: permissions?.reason || capabilities?.reason,
        permissions,
        capabilities,
      };
    },
    [equipmentPermissions, equipmentBookingCapabilities],
  );

  /**
   * Check if user has at least one equipment they can book
   *
   * @returns {boolean} - True if user can book at least one equipment
   */
  const hasBookableEquipment = useCallback(() => {
    if (!leafResources.length) return false;
    return leafResources.some((resource) => {
      const status = getEquipmentBookingStatus(resource.id);
      return !!status.canBook;
    });
  }, [leafResources, getEquipmentBookingStatus]);

  /** Store the calendar instance when initialized */
  const handleCalendarInit = useCallback(
    (event) => {
      setCalendarInstance(event.inst);
    },
    [setCalendarInstance],
  );

  const renderGroupByDropdown = () => {
    return (
      <div className="owc-groupby-wrap">
        <OwcTypography variant="body2" className="owc-groupby-title">
          Group equipment by:
        </OwcTypography>

        <OwcSelectDropdown
          className="owc-groupby-dd"
          style={{ minWidth: 220 }}
          size="compact"
          outlined
          appendTo="body"
          value={[groupBy]}
          onSelectChange={(ev) => {
            const arr = Array.isArray(ev.detail) ? ev.detail : [];
            const raw = arr[0];

            const nextId =
              typeof raw === "number"
                ? GROUP_BY_OPTIONS[raw]
                  ? GROUP_BY_OPTIONS[raw].id
                  : groupBy
                : raw;

            if (nextId && nextId !== groupBy) {
              setGroupBy(nextId);
              setEquipmentHdrOpen((v) => !v);
            }
          }}
        >
          {GROUP_BY_OPTIONS.map((group) => (
            <OwcListItem
              key={group.id}
              value={group.id}
              selected={groupBy === group.id}
            >
              {group.label}
            </OwcListItem>
          ))}
        </OwcSelectDropdown>
      </div>
    );
  };

  useEffect(() => {
    // In edit mode, a dedicated effect below controls timelineSelectedDate.
    // Ignore originalSelectedStartDate/EndDate updates from updateDatePicker:
    // React 17 applies them in separate renders, which can shift the timeline.
    if (isEditMode) return;

    if (isTimelineView && !timelineSelectedDate) {
      setTimelineSelectedDate(moment().tz(siteTimezone).toDate());
    } else if (
      originalSelectedStartDate &&
      originalSelectedEndDate &&
      !pendingReservations.length
    ) {
      // In new booking mode, use original selected dates if they were actually selected by user
      // BUT only if no pending reservations exist (to prevent jumping during block selection)
      setTimelineSelectedDate(originalSelectedStartDate);
    } else if (!timelineSelectedDate) {
      const todayDate = moment().tz(siteTimezone).toDate();
      setTimelineSelectedDate(todayDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    originalSelectedStartDate,
    originalSelectedEndDate,
    editBookingData?.dateFrom,
  ]);

  // In edit mode, initialize/re-initialize the timeline position only when the
  // booking itself changes (editBookingData?.dateFrom). Slot create/update/delete
  // never touch editBookingData, so this effect never fires during slot operations.
  // handleDateChange explicitly sets timelineSelectedDate when the user picks new dates.
  useEffect(() => {
    if (isEditMode && editBookingData?.dateFrom) {
      suppressMobiscrollDateOverrideRef.current = true;
      setTimelineSelectedDate(
        moment(editBookingData.dateFrom).tz(siteTimezone).toDate(),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editBookingData?.dateFrom]);

  // Mobiscroll's `selectedDate` prop snaps the grid to a page (week/3-day/day)
  // aligned with refDate, but its scroll-restore logic sometimes ignores the
  // requested selectedDate, leaving the timeline scrolled several days off.
  // This only matters in Edit mode (Open Full Edit), where we MUST land on
  // editBookingData.dateFrom regardless of what mobiscroll decides. For the
  // generic timeline views (/bookings, /bookings-all, /group-views) we let
  // mobiscroll handle scrolling natively via `selectedDate={calendarSelectedDate}`
  // so it positions to the start of the visible window like it always did on develop.
  const initialScrollDoneRef = useRef(false);
  useEffect(() => {
    if (!calendarInstance) return;
    if (initialScrollDoneRef.current) return;
    // Override mobiscroll's native scroll only in the wizard's step 2:
    //   * Edit mode (Open Full Edit)  -> target = editBookingData.dateFrom
    //   * New booking step1 -> step2   -> target = first pending reservation
    // In all other timeline views (/bookings, /bookings-all, /group-views)
    // we are NOT inside the wizard, so we let mobiscroll's native
    // selectedDate-driven scroll position the grid (same behaviour as develop).
    let targetDate = null;
    if (isEditMode && editBookingData?.dateFrom) {
      targetDate = editBookingData.dateFrom;
    } else if (isBookingWizard && pendingReservations?.length > 0 && pendingReservations[0]?.start) {
      targetDate = pendingReservations[0].start;
    }
    if (!targetDate) return;

    const targetMoment = moment(targetDate).tz(siteTimezone);
    const refDate = getCalendarRefDate({
      timelineSelectedDate: targetMoment.toDate(),
      siteTimezone,
      isTvMode,
      view,
    });
    const refMoment = moment(refDate).tz(siteTimezone);

    let cancelled = false;

    const scrollGridToTarget = () => {
      if (cancelled) return false;
      const calEl = calendarInstance?._el;
      const grid = calEl && calEl.querySelector(".mbsc-timeline-grid-scroll");
      if (!grid || grid.scrollWidth <= grid.clientWidth) return false;

      // Mobiscroll snaps the grid origin to the start of the refDate's
      // day (or month for month view), so we must measure the target's
      // offset from that snapped anchor, not from the raw refDate.
      let totalUnits;
      let unitsToTarget;
      if (view === TIMELINE_TYPE.MONTH) {
        totalUnits = TIMELINE_BUFFER_UNITS.MONTH * 3 + 2;
        unitsToTarget = targetMoment.diff(refMoment.clone().startOf("month"), "months", true);
      } else {
        const gridDays =
          view === TIMELINE_TYPE.WEEK
            ? TIMELINE_BUFFER_UNITS.WEEK * 5 + 30
            : view === TIMELINE_TYPE["3DAY"]
              ? TIMELINE_BUFFER_UNITS["3DAY"] * 2 + 1
              : TIMELINE_BUFFER_UNITS.DAY * 2 + 1;
        totalUnits = gridDays * 1440; // total minutes in the rendered grid
        unitsToTarget = targetMoment.diff(refMoment.clone().startOf("day"), "minutes");
      }

      const unitWidth = grid.scrollWidth / totalUnits;
      // Offset the target slightly left of viewport center so the booking is
      // immediately visible without the user having to scroll right.
      const desiredScroll = Math.max(
        0,
        Math.min(
          grid.scrollWidth - grid.clientWidth,
          unitsToTarget * unitWidth - grid.clientWidth / 3,
        ),
      );
      grid.scrollLeft = desiredScroll;
      initialScrollDoneRef.current = true;
      return true;
    };

    // Mobiscroll layouts the grid asynchronously; first paint happens after
    // onInit. Schedule a few retries to outlast its own scroll-restore.
    const timeouts = [50, 200, 500, 1000].map((ms) =>
      window.setTimeout(scrollGridToTarget, ms),
    );

    return () => {
      cancelled = true;
      timeouts.forEach((id) => window.clearTimeout(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarInstance, isEditMode, editBookingData?.dateFrom, isBookingWizard, pendingReservations?.[0]?.start, view]);

  // For equipment view, always show current time initially (only on mount)
  useEffect(() => {
    if (isTimelineView && !timelineSelectedDate) {
      const todayDate = moment().tz(siteTimezone).toDate();
      setTimelineSelectedDate(todayDate);
    }
    // eslint-disable-next-line
  }, [isTimelineView, siteTimezone]);

  // Initialize equipment view configuration
  useEffect(() => {
    if (isTimelineView && !calView?.timeline) {
      const equipmentCalView = {
        timeline: {
          type: "week",
          allDay: false,
          resolutionHorizontal: "hour",
          currentTimeIndicator: true,
        },
      };
      setCalView(equipmentCalView);
      setView("week");
    }
  }, [isTimelineView, setCalView, setView, calView]);

  useEffect(() => {
    if (
      isTimelineView &&
      resourceData &&
      resourceData.length > 0 &&
      user &&
      client
    ) {
      const scopedEquipmentIds = resolveTimelineEquipmentIdsForFetch();
      checkAllEquipmentPermissions(scopedEquipmentIds);
    }
  }, [
    isTimelineView,
    resourceData,
    user,
    client,
    checkAllEquipmentPermissions,
    resolveTimelineEquipmentIdsForFetch,
  ]);

  useEffect(() => {
    if (onPermissionCheckersReady && isTimelineView) {
      onPermissionCheckersReady({
        checkEquipmentPermission,
        checkEquipmentAvailability,
        getEquipmentBookingStatus,
        hasBookableEquipment,
        equipmentPermissions,
        equipmentBookingCapabilities,
      });
    }
  }, [
    onPermissionCheckersReady,
    isTimelineView,
    checkEquipmentPermission,
    checkEquipmentAvailability,
    getEquipmentBookingStatus,
    hasBookableEquipment,
    equipmentPermissions,
    equipmentBookingCapabilities,
  ]);

  /**
   * Change the view of timeline
   *
   * @param {any} view - Selected view
   * @returns - SetView & setCalView
   */
  const changeView = useCallback(
    (view) => {
      let calView;

      switch (view) {
        case TIMELINE_TYPE["3DAY"]:
          calView = { ...TIMELINE_CONFIG["3DAY"] };
          calView.timeline = {
            ...calView.timeline,
          };
          break;
        case TIMELINE_TYPE.WEEK:
          calView = { ...TIMELINE_CONFIG.WEEK };
          calView.timeline = {
            ...calView.timeline,
          };
          break;
        case TIMELINE_TYPE.MONTH:
          calView = { ...TIMELINE_CONFIG.MONTH };
          calView.timeline = {
            ...calView.timeline,
          };
          break;
        default:
          calView = { ...TIMELINE_CONFIG.DAY };
          calView.timeline = {
            ...calView.timeline,
          };
          break;
      }
      if (isTvMode) {
        calView.timeline = {
          ...calView.timeline,
          type: "day",
          allDay: false,
          startTime: "06:00",
          endTime: "20:00",
          timeCellStep: 60,
          timeLabelStep: 60,
          currentTimeIndicator: true,
        };
      }
      setCalView(calView);
      setView(view); // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [setCalView, isTvMode, setView],
  );

  // Watch for view changes and call changeView only once
  useEffect(() => {
    if (view) {
      changeView(view);
    }
  }, [view, changeView]);

  useEffect(() => {
    if (isTvMode) {
      if (view !== TIMELINE_TYPE.DAY) setView(TIMELINE_TYPE.DAY);

      setTimeout(
        () =>
          setCalView({
            ...TIMELINE_CONFIG.DAY,
            size: 1,
            timeline: {
              ...TIMELINE_CONFIG.DAY?.timeline,
              type: "day",
              allDay: false,
              startTime: "06:00",
              endTime: "20:00",
              timeCellStep: 60,
              timeLabelStep: 60,
              currentTimeIndicator: true,
              size: 1,
            },
          }),
        100,
      );
    }
  }, [isTvMode, setView, setCalView, view]);

  const groupByControlResource = useMemo(
    () =>
      isTimelineView && !isTvMode
        ? [
          {
            id: GROUP_BY_CONTROL_ID,
            name: "Equipment - Group by",
            isControl: true,
            eventCreation: false,
            eventDragBetweenResources: false,
            eventDragInTime: false,
            eventResize: false,
            reorder: false,
          },
        ]
        : [],
    [isTimelineView, isTvMode],
  );

  /** Custom navigation functions that move by one unit (day/week/month) */
  const navigateToPrev = useCallback(() => {
    if (!calendarInstance || !timelineSelectedDate) return;

    const currentDate = moment(timelineSelectedDate);
    let newDate;

    switch (view) {
      case TIMELINE_TYPE.DAY:
        // Move backward by 1 day
        newDate = currentDate.clone().subtract(1, "days");
        break;
      case TIMELINE_TYPE["3DAY"]:
        // Move backward by 1 day
        newDate = currentDate.clone().subtract(1, "days");
        break;
      case TIMELINE_TYPE.WEEK:
      case TIMELINE_TYPE.MONTH:
        // Move backward by 1 month
        newDate = currentDate.clone().subtract(1, "months");
        break;
      default:
        newDate = currentDate.clone().subtract(1, "days");
    }

    setTimelineSelectedDate(newDate.toDate());
    setTimeout(() => {
      calendarInstance.navigate(newDate.toDate());
    }, 0);
  }, [calendarInstance, timelineSelectedDate, view]);

  const navigateToNext = useCallback(() => {
    if (!calendarInstance || !timelineSelectedDate) return;

    const currentDate = moment(timelineSelectedDate);
    let newDate;

    switch (view) {
      case TIMELINE_TYPE.DAY:
        // Move forward by 1 day
        newDate = currentDate.clone().add(1, "days");
        break;
      case TIMELINE_TYPE.WEEK:
      case TIMELINE_TYPE.WORKWEEK:
        // Move forward by 1 week
        newDate = currentDate.clone().add(1, "weeks");
        break;
      case TIMELINE_TYPE.MONTH:
        // Move forward by 1 month
        newDate = currentDate.clone().add(1, "months");
        break;
      default:
        newDate = currentDate.clone().add(1, "days");
    }

    setTimelineSelectedDate(newDate.toDate());
    setTimeout(() => {
      calendarInstance.navigate(newDate.toDate());
    }, 0);
  }, [calendarInstance, timelineSelectedDate, view]);

  const navigateToToday = useCallback(() => {
    if (!calendarInstance) return;

    const now = moment().tz(siteTimezone);
    let today;

    switch (view) {
      case TIMELINE_TYPE.WEEK:
      case TIMELINE_TYPE["3DAY"]:
      case TIMELINE_TYPE.WORKWEEK:
        // For week view, use current date (not start of week) so the range centers properly
        today = now.clone().toDate();
        break;
      case TIMELINE_TYPE.MONTH:
        // For month view, use current date (not start of month) so the range centers properly
        today = now.clone().toDate();
        break;
      default:
        // For day view, use current day
        today = now.clone().toDate();
    }

    setTimelineSelectedDate(today);
    setTimeout(() => {
      calendarInstance.navigate(today);
    }, 0);
  }, [calendarInstance, siteTimezone, view]);

  /**
   * Create Google Calendar resources for selected calendars
   *
   * @returns {Array} - Array of Google Calendar resources
   */
  const createGoogleCalendarResources = useCallback(() => {
    if (!isGoogleLoggedIn || isTvMode) {
      return [];
    }

    const resources = [];

    // Add personal calendar resources (from selected calendars)
    if (selectedCalendarIds.length) {
      selectedCalendarIds.forEach((calendarId) => {
        const calendar = calendarData[calendarId];
        if (!calendar || !calendar.checked) return;

        resources.push({
          id: `google_calendar_${calendarId}`,
          name: formatTitle(calendar.name),
          color: "#1E8787",
          eventCreation: false,
          fixed: true,
          eventDragBetweenResources: false,
          eventDragInTime: false,
          eventResize: false,
          eventOverlap: true,
          reorder: false
        });
      });
    }

    return resources;
  }, [isGoogleLoggedIn, selectedCalendarIds, calendarData, isTvMode]);

  /**
   * Transform Google Calendar events to Mobiscroll format
   *
   * @param {Array} googleEvents - Events from Google Calendar
   * @returns {Array} - Transformed events for Mobiscroll
   */
  const transformGoogleEvents = useCallback((googleEvents) => {
    if (!googleEvents || !googleEvents.length) return [];

    const transformedEvents = [];

    googleEvents.forEach((event) => {
      // Skip working location events
      if (event.googleEvent?.eventType === "workingLocation") {
        return;
      }
      const eventTitle = event.title || event.summary || "Busy";
      const calendarId = event.googleCalendarId;

      // Use the specific calendar ID to assign the correct resource
      const resourceId = calendarId
        ? `google_calendar_${calendarId}`
        : "google_calendar_default";

      transformedEvents.push({
        id: `google_${event.id}_${calendarId || "default"}`,
        title: eventTitle,
        start: event.start,
        end: event.end,
        resource: resourceId,
        color: "#1E8787", // Set the specific color for Google Calendar events
        fcolor: "#fff", // White font color
        allDay: event.allDay || false,
        readonly: true,
        cssClass: "google-calendar-event",
        googleCalendarId: calendarId, // Keep the original calendar ID for reference
        type: "GoogleCalendar", // Add type to identify Google Calendar events
      });
    });

    return transformedEvents;
  }, []);

  /**
   * Load Google Calendar events for the current view period Splits large date
   * ranges (>90 days) into smaller chunks and makes async requests
   */
  const loadGoogleCalendarEvents = useCallback(async () => {
    if (
      isTvMode ||
      !isGoogleLoggedIn ||
      !selectedCalendarIds.length ||
      !timelineSelectedDate
    ) {
      return;
    }

    try {
      let startDate, endDate;
      const currentDate = moment(timelineSelectedDate);

      // Calculate date range based on current view
      switch (view) {
        case TIMELINE_TYPE.DAY:
          startDate = currentDate
            .clone()
            .subtract(TIMELINE_BUFFER_UNITS.DAY, "days")
            .toDate();
          endDate = currentDate
            .clone()
            .add(TIMELINE_BUFFER_UNITS.DAY, "days")
            .toDate();
          break;
        case TIMELINE_TYPE["3DAY"]:
          startDate = currentDate
            .clone()
            .subtract(TIMELINE_BUFFER_UNITS["3DAY"], "days")
            .toDate();
          endDate = currentDate
            .clone()
            .add(TIMELINE_BUFFER_UNITS["3DAY"], "days")
            .toDate();
          break;
        case TIMELINE_TYPE.WEEK:
        case TIMELINE_TYPE.WORKWEEK:
          startDate = currentDate
            .clone()
            .subtract(TIMELINE_BUFFER_UNITS.WEEK, "days")
            .toDate();
          endDate = currentDate
            .clone()
            .add(TIMELINE_BUFFER_UNITS.WEEK, "days")
            .toDate();
          break;
        case TIMELINE_TYPE.MONTH:
          startDate = currentDate
            .clone()
            .subtract(TIMELINE_BUFFER_UNITS.MONTH + 1, "months")
            .toDate();
          endDate = currentDate
            .clone()
            .add(TIMELINE_BUFFER_UNITS.MONTH + 1, "months")
            .toDate();
          break;
        default:
          startDate = currentDate
            .clone()
            .subtract(TIMELINE_BUFFER_UNITS.DAY, "days")
            .toDate();
          endDate = currentDate
            .clone()
            .add(TIMELINE_BUFFER_UNITS.DAY, "days")
            .toDate();
      }

      // Load Google Calendar events for the calculated date range
      // The GoogleCalendarContext will handle chunking automatically if needed
      await loadCalendarEvents(selectedCalendarIds, startDate, endDate);
    } catch (error) {
      console.error("Failed to load Google Calendar events:", error);
    }
  }, [
    isGoogleLoggedIn,
    selectedCalendarIds,
    timelineSelectedDate,
    view,
    loadCalendarEvents,
    isTvMode,
  ]);

  // Update Google Calendar resources when calendar selection changes
  useEffect(() => {
    const googleResources = createGoogleCalendarResources();
    setGoogleCalendarResources(googleResources);
  }, [createGoogleCalendarResources]);

  // Load Google Calendar events when view changes or calendar selection changes
  useEffect(() => {
    loadGoogleCalendarEvents();
  }, [loadGoogleCalendarEvents]);

  /**
   * Render header for timeline
   *
   * @returns - Template for event header
   */
  const renderMyHeader = () => {
    return (
      <>
        {!isTvMode && (
          <>
            <div
              className="md-work-week-today custom-nav-button today-button"
              onClick={navigateToToday}
              title="Go to today"
            >
              Today
            </div>
             <OwcIconButton
              flat={true}
              onClick={navigateToPrev}
              icon="chevron_left"
              elevated={false}
              disableRipple={true}
              className="md-work-week-prev mobi-custom-nav-button"

            />
            <OwcIconButton
              flat={true}
              onClick={navigateToNext}
              icon="chevron_right"
              elevated={false}
              disableRipple={true}
              className="md-work-week-prev mobi-custom-nav-button"/>
            <CalendarNav className="md-work-week-nav"/>
            {/* Only show view selector for non-equipment views */}
            <div style={{ marginLeft: "auto" }}>
              <OwcSelectDropdown
                style={{ width: 154 }}
                size="compact"
                outlined
                onSelectChange={(ev) => {
                  const selectedIndex = ev.detail[0];
                  if (selectedIndex === 0) {
                    changeView(TIMELINE_TYPE.DAY);
                  }
                  if (selectedIndex === 1) {
                    changeView(TIMELINE_TYPE["3DAY"]);
                  }
                  if (selectedIndex === 2) {
                    changeView(TIMELINE_TYPE.WEEK);
                  }
                  if (selectedIndex === 3) {
                    changeView(TIMELINE_TYPE.MONTH);
                  }
                }}
              >
                <OwcListItem selected={view === TIMELINE_TYPE.DAY}>
                  Day
                </OwcListItem>
                <OwcListItem selected={view === TIMELINE_TYPE["3DAY"]}>
                  3 Days
                </OwcListItem>
                <OwcListItem selected={view === TIMELINE_TYPE.WEEK}>
                  Week
                </OwcListItem>
                <OwcListItem selected={view === TIMELINE_TYPE.MONTH}>
                  Month
                </OwcListItem>
              </OwcSelectDropdown>
            </div>
          </>
        )}
      </>
    );
  };

  // FIXME: This medthod should return a class when avaiable events are rendered
  /**
   * To apply css class dynamically for events
   *
   * @param {events} event - Has event details for that hour
   * @returns - EventOccurrence
   */
  const getHourStyle = useCallback(
    (events) => {
      let hourEventClass = {};
      events.forEach((element) => {
        if (
          isEditMode &&
          (element.bookingId === editBookingData.id ||
            element.bookingId === null)
        ) {
          const row = IconStatus.find((x) => x.text === element.type);
          if (row !== undefined) {
            hourEventClass = { background: row.backgroundColor };
          }
        } else {
          if (element.type === BOOKING_TYPE.AVAILABLE) {
            const row = IconStatus.find((x) => x.text === element.type);
            if (row !== undefined) {
              hourEventClass = { background: row.backgroundColor };
            }
          }
        }
      });
      return hourEventClass;
    },
    [isEditMode, editBookingData],
  );

  /**
   * Change the date of timeline on the selected value in datepicker
   *
   * @param {any} param - Selected Date and time
   * @returns - Set date and time in timeline
   */
  const handleDateChange = useCallback(
    async (param, fromCancelAction = false) => {
      const reason = dpCloseReasonRef.current;
      dpCloseReasonRef.current = null;

      if (shouldSkipTimelineDateChangeOnCancel({
        fromCancelAction,
        pendingReservations,
        reason,
        selectedList,
      })) {
        return;
      }

      const [startDate, endDate] = param?.value || [];
      if (!startDate || !endDate) {
        return;
      }

      const onGoingEventList = getOngoingEventListForDateChange({
        isEditMode,
        isOngoing,
        pendingReservations,
      });
      const startDateUtc = changeDateToUtcString(startDate);
      const endDateUtc = changeDateToUtcString(endDate);

      clearPendingReservation();
      dateChangeInProgressRef.current = true;
      setLoading(true);
      setSelectedEndDate(endDate);
      setSelectedStartDate(startDate);

      const reservationResult = await getReservationBooking(startDateUtc, endDateUtc);
      if (!reservationResult || reservationResult.hasError) {
        setLocalFetchError(true);
        setLoading(false);
        dateChangeInProgressRef.current = false;
        return;
      }

      getEventDetails({
        startDate: startDateUtc,
        endDate: endDateUtc,
        reservationList: [...reservationResult.data, ...onGoingEventList],
        onGoingEventList,
        equipmentListOverride: null,
        isQuickEdit: false,
        includeNewEquipmentInEditMode: isEditMode,
      });
      setTimelineSelectedDate(moment(startDate).tz(siteTimezone).toDate());
      changeView(
        isSingleDayInDisplayTimezone({ displayTimeZone, endDate, startDate })
          ? TIMELINE_TYPE.DAY
          : TIMELINE_TYPE.WEEK,
      );
      setLoading(false);
      dateChangeInProgressRef.current = false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStartDate, selectedEndDate, pendingReservations],
  );

  /**
   * Call when date change in timeline
   *
   * @param {any} event - Selected Date
   * @param {any} inst - Selected instance
   * @returns - Set date and time in timeline
   */
  const onSelectedDateChange = useCallback(
    (event) => {
      if (!event.date) return;

      // Mobiscroll fires onSelectedDateChange right after we set selectedDate
      // programmatically (e.g. on entering Full Edit). In that callback,
      // `event.inst._viewDate` is snapped to the page origin and -- with our
      // 90-day refDate buffer in WEEK view -- can land a few days off from
      // the date we just set, silently shifting the timeline. Skip exactly
      // one such callback after each programmatic set.
      if (suppressMobiscrollDateOverrideRef.current) {
        suppressMobiscrollDateOverrideRef.current = false;
        return;
      }

      // FIXME: find a better solution if possible since using internal function _active is not best practice
      // refDate is a known Mobiscroll bug when selectedDate and refDate are far apart
      // because Mobiscroll internally tracks the correct selected date in _active
      // but its event.date calculation uses the buggy page/grid offset logic from refDate,
      // so we can't rely on it to get the correct date
      const correctDate = event.inst?._viewDate
        ? new Date(event.inst._viewDate)
        : event.date;

      // Only update if this is a user interaction, not initial mount
      // Check if the new date is significantly different (not just time stripped)
      if (timelineSelectedDate) {
        const currentDay = moment(timelineSelectedDate).startOf("day");
        const newDay = moment(correctDate).startOf("day");
        // Only update if day actually changed (user navigation)
        if (!currentDay.isSame(newDay)) {
          setTimelineSelectedDate(correctDate);
        }
      } else {
        // First time setting, allow it
        setTimelineSelectedDate(correctDate);
      }
    },
    [timelineSelectedDate],
  );

  /**
   * Enforce fixed number of columns in timeline view based on current view type
   *
   * @param {Eventcalendar} inst - Calendar instance
   * @param {string} view - Current view day, 3day, week, month.
   * @param {boolean} isTvMode - TV mode flag
   */
  const setupColumnWidth = useCallback((inst, view, isTvMode) => {
    const visibleColumns = (isTvMode ? 14 : VIEW_COLUMNS[view]) || 0;
    if (!visibleColumns) return;

    const calendarEl = inst?._el;
    const gridScroll = calendarEl.querySelector(".mbsc-timeline-grid-scroll");
    const resourceScroll = calendarEl.querySelector(".mbsc-timeline-resources");
    const resourceHeaderCont = calendarEl.querySelector(".mbsc-timeline-resource-header-cont");
    // Mobiscroll 5.35 inline-styles the resource HEADER column width based on
    // content (e.g. 210px for "Equipment details" + badge + chevron), but the
    // body lanes (`.mbsc-timeline-resource-bg` / `.mbsc-timeline-resource`) keep
    // the CSS-declared width (256px). The 46px mismatch makes the body resource
    // column overlap the first hour columns of the timeline. Force the body
    // lanes to match the actually rendered header width.
    const resourceWidth = resourceScroll ? resourceScroll.clientWidth : 0;
    const resourceHeaderWidth = resourceHeaderCont
      ? Math.round(resourceHeaderCont.getBoundingClientRect().width)
      : resourceWidth;
    const sidebarWidth = resourceHeaderWidth || resourceWidth;
    const viewportWidth = gridScroll.clientWidth - sidebarWidth;

    const columnWidth = Math.max(1, Math.floor(viewportWidth / visibleColumns));
    const styleId = `mobiscroll-column-override-${inst?._uid || "default"}`;
    let styleSheet = document.getElementById(styleId);
    if (!styleSheet) {
      styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      document.head.appendChild(styleSheet);
    }

    const newCss = `
          .md-timeline-template .mbsc-timeline-column,
          .md-timeline-template .mbsc-timeline-day.mbsc-timeline-column {
            width: ${columnWidth}px !important;
            min-width: ${columnWidth}px !important;
            max-width: ${columnWidth}px !important;
          }

          .md-timeline-template .mbsc-timeline-header-column,
          .md-timeline-template .mbsc-timeline-header-week,
          .md-timeline-template .mbsc-timeline-header-week-text {
            width: ${columnWidth}px !important;
            min-width: ${columnWidth}px !important;
            max-width: ${columnWidth}px !important;
          }

          /*
           * The sidebar containers (.mbsc-timeline-resource-bg and
           * .mbsc-timeline-resources) live inside a *row* flexbox, so their
           * flex-basis maps to width — pin them with the flex shorthand.
           */
          .md-timeline-template .mbsc-timeline-resource-bg,
          .md-timeline-template .mbsc-timeline-resources {
            width: ${sidebarWidth}px !important;
            min-width: ${sidebarWidth}px !important;
            max-width: ${sidebarWidth}px !important;
            flex: 0 0 ${sidebarWidth}px !important;
          }

          /*
           * The individual resource ROWS live inside a *column* flexbox
           * (.mbsc-timeline-resource-bg), so flex-basis on them maps to
           * HEIGHT, not width. Setting flex-basis here would inflate the row
           * height when there are few equipment rows (e.g. a single-item
           * group view), producing huge empty rows. Constrain WIDTH only and
           * keep mobiscroll's natural row height.
           */
          .md-timeline-template .mbsc-timeline-resource.mbsc-timeline-row.mbsc-flex-1-0.mbsc-ios.mbsc-ltr {
            width: ${sidebarWidth}px !important;
            min-width: ${sidebarWidth}px !important;
            max-width: ${sidebarWidth}px !important;
            flex-grow: 0 !important;
            flex-basis: auto !important;
          }
        `;

    styleSheet.innerHTML = newCss;

    // Break the onPageLoaded -> setupColumnWidth -> inst.refresh() ->
    // onPageLoaded feedback loop: only refresh when the generated CSS actually
    // changed (i.e. column/sidebar widths really differ). After our own
    // refresh() mobiscroll re-fires onPageLoaded, but newCss is then identical,
    // so we skip refresh() and the loop terminates after a single pass. The
    // style is still written unconditionally so column widths stay correct and
    // mobiscroll's scroll self-correction is preserved.
    if (typeof inst?.refresh === "function" && lastColumnWidthCssRef.current !== newCss) {
      lastColumnWidthCssRef.current = newCss;
      requestAnimationFrame(() => {
        try {
          inst.refresh();
        } catch (_) {
          /* noop */
        }
      });
    }
  }, []);

  /** Sets or update date picker */
  const updateDatePicker = useCallback(
    (pendingReservationsForPicker) => {
      pendingReservationsForPicker = pendingReservationsForPicker.filter(
        (reservation) =>
          reservation.start &&
          reservation.end &&
          (!isDuplicate || !reservation.bookingId),
      );
      const starts = sortBy(pendingReservationsForPicker, ["start"]);
      const ends = sortBy(pendingReservationsForPicker, ["end"]);
      slotUpdateInProgressRef.current = true;
      setSelectedStartDate(starts[0]?.start || null);
      setSelectedEndDate(ends[ends?.length - 1]?.end || null);

      if (!starts[0]?.start || !ends[ends?.length - 1]?.end) {
        resetAll();
      }
    },
    [isDuplicate, resetAll, setSelectedEndDate, setSelectedStartDate],
  );

  const syncDatePickerFromPendingReservations = useCallback(() => {
    if (
      shouldSyncDatePickerFromPendingReservations({
        selectedStartDate,
        selectedEndDate,
        pendingReservations,
      })
    ) {
      updateDatePicker(pendingReservations);
    }
  }, [pendingReservations, selectedEndDate, selectedStartDate, updateDatePicker]);

  const updateEquipmentCapabilitiesForRange = useCallback(
    async (timelineStartDay, timelineEndDay, equipmentIdsOverride = null) => {
      if (!resourceData || resourceData.length === 0 || !user) {
        return;
      }

      const resolvedEquipmentIds = Array.isArray(equipmentIdsOverride)
        ? equipmentIdsOverride.filter(Boolean)
        : [];
      const fallbackEquipmentIds = resourceData
        .map((resource) => resource?.id || resource?.inventoryId)
        .filter(Boolean);
      const equipmentIds =
        resolvedEquipmentIds.length > 0 ? resolvedEquipmentIds : fallbackEquipmentIds;

      if (!equipmentIds.length) {
        return;
      }

      const capabilityScopeKey = [timelineStartDay, timelineEndDay, equipmentIds.join(",")].join("|");
      if (lastCapabilityScopeKeyRef.current === capabilityScopeKey) {
        return;
      }

      lastCapabilityScopeKeyRef.current = capabilityScopeKey;

      try {
        await checkAllEquipmentPermissions(equipmentIds);

        const availabilityChecks = [];
        const uncachedEquipmentIds = [];

        equipmentIds.forEach((inventoryId) => {
          if (!inventoryId) {
            return;
          }

          const cacheKey = `${timelineStartDay}|${timelineEndDay}|${inventoryId}`;
          const cachedAvailability = equipmentAvailabilityCacheRef.current.get(cacheKey);
          if (cachedAvailability) {
            availabilityChecks.push({
              inventoryId,
              ...cachedAvailability,
            });
            return;
          }

          uncachedEquipmentIds.push(inventoryId);
        });

        if (uncachedEquipmentIds.length > 0) {
          const fetchedAvailabilityChecks = await Promise.all(
            uncachedEquipmentIds.map(async (inventoryId) => {
              const availability = await checkEquipmentAvailability(
                inventoryId,
                timelineStartDay,
                timelineEndDay,
              );

              const cacheKey = `${timelineStartDay}|${timelineEndDay}|${inventoryId}`;
              if (!availability?.hasError) {
                equipmentAvailabilityCacheRef.current.set(cacheKey, availability);
              }

              return {
                inventoryId,
                ...availability,
              };
            }),
          );

          availabilityChecks.push(...fetchedAvailabilityChecks);
        }

        setEquipmentBookingCapabilities((prevCapabilities) => {
          const nextCapabilities = new Map(prevCapabilities);

          availabilityChecks.forEach((check) => {
            if (!check?.inventoryId) {
              return;
            }

            const currentCapabilities = nextCapabilities.get(check.inventoryId) || {};
            nextCapabilities.set(check.inventoryId, {
              ...currentCapabilities,
              hasConflicts: check.hasConflicts,
              existingBookings: check.existingBookings,
              availableSlots: check.availableSlots,
            });
          });

          return nextCapabilities;
        });
      } catch (error) {
        console.error(
          "Error checking equipment permissions and availability:",
          error,
        );
        lastCapabilityScopeKeyRef.current = "";
      }
    },
    [checkAllEquipmentPermissions, checkEquipmentAvailability, resourceData, user],
  );

  const fetchTimelineReservationEntriesWithCache = useCallback(
    async (timelineStartDay, timelineEndDay, scopedEquipmentIds) => {
      const rangeCacheKey = `${timelineStartDay}|${timelineEndDay}`;
      const fetchedIdsSet =
        reservationRowsFetchedByRangeRef.current.get(rangeCacheKey) || new Set();
      const inFlightIdsSet =
        reservationRowsInFlightByRangeRef.current.get(rangeCacheKey) || new Set();
      const equipmentIdsToFetch = scopedEquipmentIds.filter(
        (inventoryId) =>
          !fetchedIdsSet.has(inventoryId) && !inFlightIdsSet.has(inventoryId),
      );

      if (equipmentIdsToFetch.length === 0) {
        return {
          hasError: false,
          reservationData: [],
          equipmentIdsToFetch,
        };
      }

      equipmentIdsToFetch.forEach((inventoryId) => inFlightIdsSet.add(inventoryId));
      reservationRowsInFlightByRangeRef.current.set(rangeCacheKey, inFlightIdsSet);

      try {
        const reservationResult = await getReservationBooking(
          timelineStartDay,
          timelineEndDay,
          equipmentIdsToFetch,
        );

        if (!reservationResult || reservationResult.hasError) {
          return {
            hasError: true,
            reservationData: [],
            equipmentIdsToFetch,
          };
        }

        equipmentIdsToFetch.forEach((inventoryId) => fetchedIdsSet.add(inventoryId));
        reservationRowsFetchedByRangeRef.current.set(rangeCacheKey, fetchedIdsSet);

        return {
          hasError: false,
          reservationData: reservationResult.data || [],
          equipmentIdsToFetch,
        };
      } finally {
        equipmentIdsToFetch.forEach((inventoryId) => inFlightIdsSet.delete(inventoryId));
        if (inFlightIdsSet.size === 0) {
          reservationRowsInFlightByRangeRef.current.delete(rangeCacheKey);
        } else {
          reservationRowsInFlightByRangeRef.current.set(rangeCacheKey, inFlightIdsSet);
        }
      }
    },
    [getReservationBooking],
  );

  const handleTimelinePageLoading = useCallback(
    async (event) => {
      setLoading(true);
      const { timelineStartDay, timelineEndDay } = getTimelineBoundsFromPageEvent(event);
      const scopedEquipmentIds = await resolveFirstGroupedTimelineScopeFromRows(event);
      if (scopedEquipmentIds.length > 0) {
        resourceCatchUpDoneRef.current = true;
      }
      lastTimelineBoundsRef.current = { timelineStartDay, timelineEndDay };
      lastVisibleScopeKeyRef.current = scopedEquipmentIds.join(",");
      markEquipmentRowsLoading(scopedEquipmentIds);

      try {
        await updateEquipmentCapabilitiesForRange(
          timelineStartDay,
          timelineEndDay,
          scopedEquipmentIds,
        );

        const {
          hasError,
          reservationData,
        } = await fetchTimelineReservationEntriesWithCache(
          timelineStartDay,
          timelineEndDay,
          scopedEquipmentIds,
        );

        if (hasError) {
          setLocalFetchError(true);
          return;
        }

        if (reservationData.length > 0) {
          setEventsData((prevEvents) =>
            mergeTimelineReservations({ prevEvents, reservationData }),
          );
        }

        syncDatePickerFromPendingReservations();
      } finally {
        unmarkEquipmentRowsLoading(scopedEquipmentIds);
        setLoading(false);
      }
    },
    [
      fetchTimelineReservationEntriesWithCache,
      markEquipmentRowsLoading,
      resolveFirstGroupedTimelineScopeFromRows,
      setEventsData,
      setLoading,
      syncDatePickerFromPendingReservations,
      unmarkEquipmentRowsLoading,
      updateEquipmentCapabilitiesForRange,
    ],
  );

  const onTimelineVirtualLoading = useCallback(
    (args = {}) => {
      if (!isTimelineView) {
        return;
      }

      if (!timelineFetchGateReadyRef.current) {
        pendingTimelineVirtualArgsRef.current = args;
        return;
      }

      const bounds = lastTimelineBoundsRef.current;
      if (!bounds?.timelineStartDay || !bounds?.timelineEndDay) {
        return;
      }

      if (virtualLoadingDebounceRef.current) {
        window.clearTimeout(virtualLoadingDebounceRef.current);
      }

      virtualLoadingDebounceRef.current = window.setTimeout(async () => {
        const scopedEquipmentIds = resolveTimelineEquipmentIdsForFetch(args);
        const nextScopeKey = scopedEquipmentIds.join(",");
        if (!nextScopeKey || nextScopeKey === lastVisibleScopeKeyRef.current) {
          return;
        }

        lastVisibleScopeKeyRef.current = nextScopeKey;
        const requestId = ++virtualLoadingRequestIdRef.current;
        markEquipmentRowsLoading(scopedEquipmentIds);

        try {
          await updateEquipmentCapabilitiesForRange(
            bounds.timelineStartDay,
            bounds.timelineEndDay,
            scopedEquipmentIds,
          );
          if (requestId !== virtualLoadingRequestIdRef.current) {
            return;
          }

          const {
            hasError,
            reservationData,
          } = await fetchTimelineReservationEntriesWithCache(
            bounds.timelineStartDay,
            bounds.timelineEndDay,
            scopedEquipmentIds,
          );
          if (requestId !== virtualLoadingRequestIdRef.current) {
            return;
          }

          if (hasError) {
            setLocalFetchError(true);
            return;
          }

          if (reservationData.length > 0) {
            setEventsData((prevEvents) =>
              mergeTimelineReservations({ prevEvents, reservationData }),
            );
          }
        } finally {
          unmarkEquipmentRowsLoading(scopedEquipmentIds);
        }
      }, 400);
    },
    [
      fetchTimelineReservationEntriesWithCache,
      isTimelineView,
      markEquipmentRowsLoading,
      resolveTimelineEquipmentIdsForFetch,
      setEventsData,
      unmarkEquipmentRowsLoading,
      updateEquipmentCapabilitiesForRange,
    ],
  );

  useEffect(() => {
    return () => {
      if (virtualLoadingDebounceRef.current) {
        window.clearTimeout(virtualLoadingDebounceRef.current);
      }

      if (timelineFetchGateTimerRef.current) {
        window.clearTimeout(timelineFetchGateTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    lastVisibleScopeKeyRef.current = "";
    lastCapabilityScopeKeyRef.current = "";
    equipmentAvailabilityCacheRef.current = new Map();
    reservationRowsFetchedByRangeRef.current = new Map();
    reservationRowsInFlightByRangeRef.current = new Map();
  }, [sortedTimelineLeafResourceIds, timelineSelectedDate, view]);

  useEffect(() => {
    if (
      !isTimelineView
      || !sortedTimelineLeafResourceIds.length
      || !lastTimelineBoundsRef.current
      || resourceCatchUpDoneRef.current
    ) {
      return;
    }

    const { timelineStartDay, timelineEndDay } = lastTimelineBoundsRef.current;
    const rangeCacheKey = `${timelineStartDay}|${timelineEndDay}`;
    const fetchedIdsSet =
      reservationRowsFetchedByRangeRef.current.get(rangeCacheKey);
    if (fetchedIdsSet && fetchedIdsSet.size > 0) {
      return;
    }

    resourceCatchUpDoneRef.current = true;

    handleTimelinePageLoading({
      firstDay: new Date(timelineStartDay),
      lastDay: new Date(timelineEndDay),
    });
  }, [handleTimelinePageLoading, isTimelineView, sortedTimelineLeafResourceIds]);

  useEffect(() => {
    loadingEquipmentRequestCountRef.current = new Map();
    setLoadingEquipmentIds(new Set());
  }, [sortedTimelineLeafResourceIds, timelineSelectedDate, view]);

  const handleStandardPageLoading = useCallback(
    async (event) => {
      if (
        shouldSkipStandardPageLoading({
          isDateChangeInProgress: dateChangeInProgressRef.current,
          isSlotUpdateInProgress: slotUpdateInProgressRef.current,
        })
      ) {
        slotUpdateInProgressRef.current = false;
        return;
      }

      setLoading(true);
      setEventsData([]);
      const { timelineStartDay, timelineEndDay } = getTimelineBoundsFromPageEvent(event);

      try {
        const reservationResult = await getReservationBooking(
          timelineStartDay,
          timelineEndDay,
        );

        if (!reservationResult || reservationResult.hasError) {
          setLocalFetchError(true);
          return;
        }

        const reservationData = reservationResult.data || [];

        if (
          shouldLoadLatestEventDetails({
            newEquipmentIds,
            selectedStartDate,
            selectedEndDate,
          })
        ) {
          getLatestEventDetailsOnly(
            getLatestEventDetailsStartDate({
              isOngoing,
              isEditMode,
              siteTimezone,
              originalSelectedStartDate,
              selectedStartDate,
            }),
            changeDateToUtcString(originalSelectedEndDate ?? selectedEndDate),
            reservationData,
            newEquipmentIds,
          );
          setNewEquipmentIds([]);
          return;
        }

        const nextEvents = buildStandardPageEvents({
          reservationData,
          pendingReservations,
        });

        setEventsData(() => nextEvents);
        syncDatePickerFromPendingReservations();
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingReservations, selectedStartDate, selectedEndDate, newEquipmentIds, isOngoing, isEditMode],
  );

  const onPageLoading = useCallback(
    async (event, _inst) => {
      if (loading) return;

      if (isTimelineView) {
        if (!timelineFetchGateReadyRef.current) {
          pendingTimelinePageEventRef.current = event;
          return;
        }

        await handleTimelinePageLoading(event);
        return;
      }

      await handleStandardPageLoading(event);
    },
    [
      handleStandardPageLoading,
      handleTimelinePageLoading,
      isTimelineView,
      loading,
    ],
  );

  useEffect(() => {
    if (!TIMELINE_FETCH_GATE_ENABLED) {
      timelineFetchGateReadyRef.current = true;
      pendingTimelinePageEventRef.current = null;
      pendingTimelineVirtualArgsRef.current = null;
      return;
    }

    if (!isTimelineView) {
      timelineFetchGateReadyRef.current = true;
      pendingTimelinePageEventRef.current = null;
      pendingTimelineVirtualArgsRef.current = null;
      return;
    }

    timelineFetchGateReadyRef.current = false;
    pendingTimelinePageEventRef.current = null;
    pendingTimelineVirtualArgsRef.current = null;

    if (timelineFetchGateTimerRef.current) {
      window.clearTimeout(timelineFetchGateTimerRef.current);
    }

    timelineFetchGateTimerRef.current = window.setTimeout(async () => {
      timelineFetchGateReadyRef.current = true;

      const pendingPageEvent = pendingTimelinePageEventRef.current;
      pendingTimelinePageEventRef.current = null;
      if (pendingPageEvent) {
        await handleTimelinePageLoading(pendingPageEvent);
        return;
      }

      const pendingVirtualArgs = pendingTimelineVirtualArgsRef.current;
      pendingTimelineVirtualArgsRef.current = null;
      if (pendingVirtualArgs) {
        onTimelineVirtualLoading(pendingVirtualArgs);
      }
    }, TIMELINE_FETCH_CONTEXT_SETTLE_MS);

    return () => {
      if (timelineFetchGateTimerRef.current) {
        window.clearTimeout(timelineFetchGateTimerRef.current);
      }
    };
  }, [
    getReservationBooking,
    handleTimelinePageLoading,
    isTimelineView,
    onTimelineVirtualLoading,
  ]);

  /**
   * Render Custom Hour for timeline
   *
   * @returns - Template for Custom Hour
   */
  const renderCustomHour = (args) => {
    return (
      <div className={"md-date-header-hour "} style={getHourStyle(args.events)}>
        {formatDate("h:mm A", args.date)}
      </div>
    );
  };

  /**
   * Render event icons based on booking type
   *
   * @param {string} type - Event type
   * @returns - Icons for events
   */
  const renderBookingIcon = (type, eventData) => {
    // Use the same function as PopoverBookingTitle component
    // to maintain consistency across the application
    const displayText = getBookingDisplayText({
      type: type,
      assistanceDeclaration: eventData?.assistanceDeclaration,
    });
    const iconDetails = IconStatus.find((item) => item.text === displayText);

    if (iconDetails?.icon) {
      return iconDetails.icon;
    }

    // Fallback to category lookup if no icon found by display text
    const category = getBookingCategory(type);
    const iconName = IconStatus.find((item) => item.text === category)?.icon;
    if (iconName) {
      return iconName;
    }
    //FIXME: below left for backward compatibility with old bookings
    switch (type) {
      case BOOKING_TYPE.BOOKED:
        return "schedule_on";
      case BOOKING_TYPE.RESERVED:
        return "order_approval";
      case BOOKING_TYPE.MAINTENANCE:
        return "test_result";
      case BOOKING_TYPE.AVAILABLE:
        return "confirm";
      default:
        return "confirm";
    }
  };

  /**
   * Render Custom events for timeline
   *
   * @returns - Template for Custom events
   */
  const renderCustomScheduleEvent = (data) => {
    // Use custom equipment event renderer if available and in equipment view
    if (isTimelineView && renderCustomEquipmentEvent) {
      return renderCustomEquipmentEvent(data);
    }

    return (
      <TimelineBookingEvent
        data={data}
        eventHover={eventHover}
        setEventHover={setEventHover}
        usersList={usersList}
        isEditMode={isEditMode}
        editBookingData={editBookingData}
        isDuplicate={isDuplicate}
        eventSlotDelete={eventSlotDelete}
        manipulatePopoverContent={manipulatePopoverContent}
        bookingPopUpDispatchAction={dispatchPopupWithLoader}
        handleOnClose={handleOnClose}
        renderBookingIcon={renderBookingIcon}
        view={view}
      />
    );
  };

  /**
   * To handle overlap of events in time line
   *
   * @param {any} event - Args of created event
   * @param {inst} inst - Instanse of the event calendar
   * @returns - Boolean based on filterred events length
   */
  const hasOverlap = useCallback((event, inst) => {
    if (event.isInformationBooking || event.slot === "info") {
      return false;
    }
    const isBookingAdmin = user?.roles?.some((role) => role === "BookingAdmin");
    // Admins are allowed to overlap with regular bookings when creating a new slot
    // (both in timeline drag&drop and in the Book Equipment wizard step 2).
    // The conflicting slot will be forced to Information type in step 3.
    // Editing/duplicating existing bookings keeps the original overlap protection.
    if (isBookingAdmin && !isEditMode && !isDuplicate) {
      return false;
    }
    const events = inst
      .getEvents(event.start, event.end)
      .filter(
        (e) =>
          e.id !== event.id &&
          e.resource === event.resource &&
          !e.isInformationBooking &&
          e.slot !== "info",
      );
    return events.length > 0;
    // eslint-disable-next-line
  }, [user, isEditMode, isDuplicate]);
  /**
   * To handle past events in time line
   *
   * @param {any} args - Args of created event
   * @returns - Boolean based on filterred events length
   */
  const hasPastEvent = useCallback(
    (args) => {
      const now = moment.tz(siteTimezone);
      const eventStart = moment(args.event.start).tz(siteTimezone);
      const eventEnd = moment(args.event.end).tz(siteTimezone);
      if (eventEnd.isSameOrBefore(now)) return true;
      const currentSlotStart = moment.utc(getCurrentHalfHourSlotUtc(siteTimezone)).tz(siteTimezone);
      return eventStart.isBefore(currentSlotStart);
    }, [siteTimezone]);
  /**
   * To handle past events in time line
   *
   * @param {any} args - Args of created event
   * @returns - Boolean based on filterred events length
   */
  const hasPastEndDate = useCallback(
    (args) => {
      const currentTime = moment.tz(siteTimezone);
      const eventEndTime = moment(args.event.end).tz(siteTimezone);
      return currentTime.isAfter(eventEndTime);
      // eslint-disable-next-line
    },
    [siteTimezone],
  );

  useEffect(() => {
    if (!isTimelineView) {
      setGroupBy("EQUIPMENT");
    }
  }, [isTimelineView]);

  const popupText = useMemo(() => (
    <OwcTypography>
      Please select a valid date range in the future. <br />
      Adjusting past dates/time is not allowed.
    </OwcTypography>
  ), []);

  const dispatchPopupWithLoader = useCallback(
    (popupAction) => {
      bookingPopUpDispatchAction({
        type: "loading",
        payload: { loading: true },
      });

      const dispatchPopup = () => {
        bookingPopUpDispatchAction(popupAction);
        bookingPopUpDispatchAction({
          type: "loading",
          payload: { loading: false },
        });
      };

      if (
        typeof window !== "undefined" &&
        typeof window.requestAnimationFrame === "function"
      ) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(dispatchPopup);
        });
      } else {
        setTimeout(dispatchPopup, 0);
      }
    },
    [bookingPopUpDispatchAction],
  );

  const handleOnClose = useCallback(() => {
    bookingPopUpDispatchAction({
      type: "reset",
    });
  }, [bookingPopUpDispatchAction]);
  /**
   * To render the model name with close icon in mobiscroll calendar
   *
   * @returns {component} - Model name with cross icon
   */
  const customResource = (resource) => {
    // Check if this is a Google Calendar resource
    const isGoogle = resource.id?.startsWith("google_calendar_");
    const isControl = resource.isControl && resource.id === GROUP_BY_CONTROL_ID;
    const isGroup =
      (Array.isArray(resource.children) && resource.children.length > 0) ||
      String(resource.id || "").startsWith("grp_");
    const rowClass = `md-resource-header-template-cont ${isControl
      ? "md-row-control"
      : isGoogle
        ? "md-row-google"
        : isGroup
          ? "md-row-group"
          : "md-row-leaf"
      }`;

    if (isControl) {
      const totalEquipment = leafResources.length || 0;

      return (
        <div className={rowClass}>
          <div
            className="md-resource-header-template-title"
            style={{ cursor: "pointer", width: "100%" }}
            onClick={() => setEquipmentHdrOpen((v) => !v)}
          >
            <div
              className="md-resource-header-template-name"
              style={{ width: "100%" }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: 8 }}
                id={`attr_groupby_tooltip`}
              >
                <OwcTypography variant="body2">Equipment details </OwcTypography>
                <OwcBadge
                  type="active"
                  style={{ marginLeft: "auto", width: 24, height: 24 }}
                >
                  {totalEquipment}
                </OwcBadge>
                <OwcIcon
                  name={isEquipmentHdrOpen ? "chevron_up" : "chevron_down"}
                  style={{ cursor: "pointer" }}
                />
              </div>
              <OwcTooltip
                anchor={`attr_groupby_tooltip`}
                style={{ display: isEquipmentHdrOpen ? "none" : "block" }}
              >
                Click to toggle grouping
              </OwcTooltip>
            </div>
          </div>

          {isEquipmentHdrOpen && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {renderGroupByDropdown()}
            </div>
          )}
        </div>
      );
    }

    if (isGoogle) {
      // Extract the calendar ID from the resource ID
      const calendarId = resource.id.replace("google_calendar_", "");

      return (
        <div className={rowClass}>
          <div className="md-resource-header-template-name">
            <div style={{ display: "flex", alignItems: "center" }}>
              <OwcTypography
                variant="body2"
                style={{
                  marginRight: "10px",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {resource.name}
              </OwcTypography>

              {/* X button to deselect Google Calendar */}
              <OwcIconButton
                icon="circle_clear"
                type="filled"
                flat
                style={{
                  marginLeft: "auto",
                  fontSize: "16px",
                }}
                onClick={(ev) => {
                  // Use toggleCalendar to deselect this calendar
                  toggleCalendar(calendarId, false);
                  ev.stopPropagation();
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    // Regular equipment resource handling
    const permissions = equipmentPermissions.get(resource.id);
    const capabilities = equipmentBookingCapabilities.get(resource.id);
    const normalizedResourceId = String(resource?.id || "");
    const isLeafResourceRow = normalizedTimelineLeafResourceIdSet.has(normalizedResourceId);
    const isPermissionResolved = permissions !== undefined;
    const isCapabilityResolved = capabilities !== undefined;
    const isCapabilityResolvedForRow = isPermissionResolved && isCapabilityResolved;
    const canBook = isCapabilityResolvedForRow && permissions?.canBook && capabilities?.canCreate;
    const reason = permissions?.reason || capabilities?.reason;
    const resourceMeta = normalizeAccessControlDataShape({
      ...(equipmentMetaById.get(resource.id) || {}),
      ...getCachedAccessConfigEnrichment(resource.id),
      ...resource,
    });
    // Prefer the resolved permission (same source that drives canBook) so the
    // lock icon / greyed label never disagree with the booking decision. Fall
    // back to a synchronous compute only before the permission has resolved.
    const fallbackAccessControl = getBookingAccessControlState(resourceMeta, user);
    const accessControlState = isPermissionResolved
      ? permissions?.accessControlState ?? fallbackAccessControl.status
      : fallbackAccessControl.status;
    const cidmGroupName = isPermissionResolved
      ? permissions?.cidmGroupName ?? fallbackAccessControl.cidmGroupName
      : fallbackAccessControl.cidmGroupName;
    const isTrainingRequired =
      isTimelineView
      && !isGroup
      && accessControlState === accessControlStatus.trainingRequired
      && Boolean(cidmGroupName);
    const isAdminAccess = Boolean(permissions?.isAdminAccess && !isTrainingRequired);
    const isRowLoading =
      isTimelineView
      && !isGoogle
      && !isControl
      && !isGroup
      && isLeafResourceRow
      && (loadingEquipmentIds.has(normalizedResourceId) || !isCapabilityResolvedForRow);

    return (
      <div className={rowClass} data-resource-id={resource.id}>
        <div className="md-resource-header-template-name">
          <div
            style={{ display: "flex", alignItems: "center", cursor: "pointer"}}
            onClick={() => {
              manipulatePopoverContent({
                userSelectionType: RESOURCE,
                resourceId: resource?.id,
              });
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                flex: "1 1 auto",
                overflow: "hidden",
              }}
            >
              <OwcTypography
                variant="body2"
                style={{
                  marginRight: "10px",
                  minWidth: 0,
                  fontSize: "14px",
                  lineHeight: isGroup ? "165%" : "140%",
                  fontFamily: "Roche Sans",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: !isTimelineView
                    ? "inherit"
                    : canBook || isGroup
                      ? "inherit"
                      : "#999",
                  fontStyle: isAdminAccess ? "italic" : "normal",
                }}
                id={resource.id}
              >
                {resource.name}
                {isTimelineView && isTrainingRequired && (
                  <OwcIconButton
                    icon="lock_on"
                    flat
                    title={`Training required: ${cidmGroupName}`}
                    aria-label={`Training required for ${resource.name}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      verticalAlign: "middle",
                      marginLeft: "4px",
                      marginRight: "4px",
                      flexShrink: 0,
                      color: "#cc0033",
                    }}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      dispatchOpenCidmDialog({
                        cidmGroupName,
                        cidmGroupCidmUrl: resourceMeta.cidm_group_cidm_url,
                        cidmGroupMoreInfoUrl: resourceMeta.cidm_group_more_info_url,
                      });
                    }}
                  />
                )}
                {isAdminAccess && (
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#FF9800",
                      fontWeight: "bold",
                      marginLeft: "4px",
                    }}
                  >
                    (Admin)
                  </span>
                )}
              </OwcTypography>
            </div>
            <OwcTooltip anchor={resource.id}>{resource.name}</OwcTooltip>

            {isTimelineView && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "auto",
                  marginRight: "8px",
                  gap: "6px",
                }}
              >
                {isRowLoading && (
                  <span className="timeline-row-loading-indicator" title="Loading bookings for this equipment">
                    <OwcProgressSpinner diameter={12} />
                  </span>
                )}
                {!isRowLoading && canBook && !isTrainingRequired ? (
                  <OwcIcon
                    name="check_circle"
                    style={{
                      color: isAdminAccess ? "#FF9800" : "#4CAF50",
                      fontSize: "16px",
                    }}
                    title={
                      isAdminAccess
                        ? "Bookable (Admin access only)"
                        : "You can book this equipment"
                    }
                  />
                ) : !isRowLoading ? (
                  <OwcIcon
                    name="block"
                    style={{ color: "#F44336", fontSize: "16px" }}
                    title={`Cannot book: ${reason || "No permission"}`}
                  />
                ) : null}
              </div>
            )}

            {!isTimelineView && (
              <OwcIconButton
                icon="circle_clear"
                type="filled"
                flat
                style={{ marginLeft: "auto" }}
                onClick={(ev) => {
                  const isAnyOnGoingEvent = pendingReservations.filter(
                    (x) => x.resource === resource?.id && x.isOngoingEventFlag,
                  );
                  if (isEditMode && isAnyOnGoingEvent.length > 0) {
                    dispatchPopupWithLoader({
                      type: "onGoingBooking",
                      payload: {
                        onApprove: handleOnClose,
                      },
                    });
                  } else {
                    dispatchPopupWithLoader({
                      type: "futureBooking",
                      payload: {
                        onApprove: () => handlerOnDelete(resource?.id),
                        onclose: handleOnClose,
                      },
                    });
                  }
                  setDeleteResourceId(resource?.id);
                  setCancelStatus(true);
                  ev.stopPropagation();
                }}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  /**
   * To show the number of selected events for each equipment
   *
   * @returns {JSX.Element} - Show the number of selected events with model
   *   header
   */
  const customResourceHeader = () => {
    const totalEquipment = leafResources?.length || 0;
    const totalGoogleCalendars = googleCalendarResources?.length || 0;

    return (
      <>
        {/* Google Calendar section header */}
        {!isTvMode && (totalGoogleCalendars > 0 || isTimelineView) && (
          <div className="md-resource-header-template-title">
            <div className="md-resource-header-template-name">
              <div style={{ display: "flex", alignItems: "center" }}>
                <OwcTypography variant="body2" style={{ marginRight: "10px" }}>
                  Calendar details
                </OwcTypography>
                <OwcBadge
                  type="active"
                  style={{
                    marginLeft: "auto",
                    width: "24px",
                    height: "24px",
                    backgroundColor:
                      totalGoogleCalendars > 0 ?? "#A0A0A0",
                  }}
                >
                  {totalGoogleCalendars || 0}
                </OwcBadge>
              </div>
            </div>
          </div>
        )}

        {/* Equipment section header */}
        {totalEquipment > 0 && (!isTimelineView || isTvMode) && (
          <div className="md-resource-header-template-title">
            <div className="md-resource-header-template-name">
              <div style={{ display: "flex", alignItems: "center" }}>
                <OwcTypography variant="body2" style={{ marginRight: "10px" }}>
                  Equipment details
                </OwcTypography>
                <OwcBadge
                  type="active"
                  style={{
                    marginLeft: "auto",
                    width: "22px",
                    height: "21px",
                  }}
                >
                  {totalEquipment}
                </OwcBadge>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const hasHiddenOverlap = useCallback(
    async (event) => {
      const inventoryId = event.resource;

      // +1h for comparisons (the selected event is shifted one hour backward)
      const adjustedStart = new Date((event?.start instanceof Date ? event.start : new Date(event?.start)).getTime() + 3600000);
      const adjustedEnd = new Date((event?.end instanceof Date ? event.end : new Date(event?.end)).getTime() + 3600000);

      try {
        const res = await client.query({
          query: GET_BOOKING_ENTRIES_BY_SITE_AND_INVENTORY_ID,
          variables: {
            site: user?.site,
            inventoryId: { eq: inventoryId },
            limit: 1000,
          },
          dataPath: ["data", "bookingEntriesBySiteAndInventoryId"],
          fetchPolicy: "no-cache",
        });

        if (res?.errors && res.errors.length > 0) {
          throw new Error("GraphQL validation error");
        }

        const bookingEntries = res?.data?.bookingEntriesBySiteAndInventoryId;
        const activeBookings = bookingEntries?.items?.filter((entry) => {
          if (entry.active) {
            return entry.start < adjustedEnd && entry.end > adjustedStart;
          }
          return false;
        });

        return { hasOverlap: activeBookings.length > 0, hasError: false };
      } catch (err) {
        console.error("hasHiddenOverlap query error", err);
        return { hasOverlap: false, hasError: true };
      }
    },
    [client, user?.site],
  );

  const normalizeCreatedEvent = useCallback(
    (event) => {
      const hourMs = 60 * 60 * 1000;
      const minDurationByViewMs = {
        [TIMELINE_TYPE.WEEK]: 2 * hourMs,
        [TIMELINE_TYPE["3DAY"]]: 1 * hourMs,
      };
      const minDurationMs = minDurationByViewMs[view] ?? 0;
      const eventStart = new Date(event.start);
      const durationMs = new Date(event.end).getTime() - eventStart.getTime();

      if (minDurationMs > 0 && durationMs < minDurationMs) {
        return {
          ...event,
          end: new Date(eventStart.getTime() + minDurationMs),
        };
      }

      return event;
    },
    [view],
  );

  const getCreateBookingDenyReason = (permissions, capabilities) =>
    permissions?.reason ||
    capabilities?.reason ||
    "No permission to book this equipment";

  const addPendingEventRow = useCallback((eventRow) => {
    const newPendingReservations = [...pendingReservations, eventRow];
    updatePendingReservation(newPendingReservations);
    setEventsData((prev) => [...prev, eventRow]);
    updateDatePicker(newPendingReservations);
  }, [pendingReservations, setEventsData, updatePendingReservation, updateDatePicker]);

  const validateCreatedEvent = useCallback((args, inst, normalizedEvent) => {
    if (isTimelineEditMode) {
      return {
        type: "toast",
        message: "Finish or cancel the current change before creating a new slot",
      };
    }

    if (hasServerError) {
      return {
        type: "snackbar",
        message: "Unexpected Server Error",
      };
    }

    if (isTimelineView) {
      const permissions = equipmentPermissions.get(normalizedEvent.resource);
      const capabilities = equipmentBookingCapabilities.get(normalizedEvent.resource);

      if (!permissions?.canBook || !capabilities?.canCreate) {
        return {
          type: "toast",
          message: `Cannot create booking: ${getCreateBookingDenyReason(permissions, capabilities)}`,
        };
      }
    }

    if (hasPastEvent({ ...args, event: normalizedEvent })) {
      return { type: "toast", message: "Past date not allowed" };
    }

    if (hasOverlap(normalizedEvent, inst)) {
      return { type: "toast", message: "Overlap not allowed" };
    }

    return null;
  }, [
    equipmentBookingCapabilities,
    equipmentPermissions,
    hasServerError,
    hasOverlap,
    hasPastEvent,
    isTimelineEditMode,
    isTimelineView,
  ]);

  const handleCreateValidationError = useCallback((validationError) => {
    if (!validationError) return;

    if (validationError.type === "snackbar") {
      snackbarService.show({
        message: validationError.message,
        type: "error",
      });
      return;
    }

    toast({ message: validationError.message });
  }, []);

  const getCommonEventUpdateError = useCallback((args, inst) => {
    if (isDuplicate && !args.event.id?.startsWith("available_")) {
      return { type: "silent" };
    }

    if (hasOverlap(args.event, inst)) {
      return { type: "toast", message: "Overlap not allowed" };
    }

    const currentHalfHourSlotUtc = getCurrentHalfHourSlotUtc(siteTimezone);
    const oldStartUtc = changeDateToUtcString(args.oldEvent?.start || args.event.start);
    const newStartUtc = changeDateToUtcString(args.event.start);

    if (newStartUtc < currentHalfHourSlotUtc && newStartUtc !== oldStartUtc) {
      return { type: "toast", message: "Start date in the past not allowed" };
    }

    return null;
  }, [hasOverlap, isDuplicate, siteTimezone]);

  const getStandardEventUpdateError = useCallback((args) => {
    if (isEditMode && args.event.isOngoingEventFlag) {
      if (
        moment(args.event.start).tz(siteTimezone).format() !==
        moment(args.oldEvent.start).tz(siteTimezone).format()
      ) {
        return { type: "toast", message: "Can't change start date." };
      }

      if (hasPastEndDate(args)) {
        return {
          type: "popup",
          payload: {
            onApprove: handleOnClose,
            title: "Date range adjustment not permitted",
            content: popupText,
          },
        };
      }

      return null;
    }

    if (hasPastEvent(args)) {
      return { type: "toast", message: "Past date not allowed" };
    }

    return null;
  }, [handleOnClose, hasPastEndDate, hasPastEvent, isEditMode, popupText, siteTimezone]);

  const handleStandardEventUpdateError = useCallback((updateError) => {
    if (!updateError || updateError.type === "silent") return false;

    if (updateError.type === "popup") {
      dispatchPopupWithLoader({
        type: "eventBooking",
        payload: updateError.payload,
      });
      return true;
    }

    toast({ message: updateError.message });
    return true;
  }, [dispatchPopupWithLoader]);

  const getExistingBookingEditError = useCallback((args, nowUtc) => {
    return getExistingBookingEditErrorMessage({
      bookingId: args.event.bookingId,
      canEditExistingBooking: canEditExistingBooking(args.event, user),
      eventId: args.event.id,
      newResource: args.event.resource,
      newStart: changeDateToUtcString(args.event.start),
      nowUtc,
      oldEnd: changeDateToUtcString(args.oldEvent.end),
      oldResource: args.oldEvent.resource,
      oldStart: changeDateToUtcString(args.oldEvent.start),
      pendingReservationsLength: pendingReservations.length,
      permissions: equipmentPermissions.get(args.event.resource),
      timelineEditDraft,
    });
  }, [equipmentPermissions, pendingReservations.length, timelineEditDraft, user]);

  const applyExistingBookingDraft = useCallback((args) => {
    const eventId = args.event.id;
    const bookingId = args.event.bookingId;
    const oldStart = changeDateToUtcString(args.oldEvent.start);
    const oldEnd = changeDateToUtcString(args.oldEvent.end);
    const oldResource = args.oldEvent.resource;
    const newStart = changeDateToUtcString(args.event.start);
    const newEnd = changeDateToUtcString(args.event.end);
    const newResource = args.event.resource;

    const baseOriginal =
      timelineEditDraft && timelineEditDraft.entryId === eventId
        ? timelineEditDraft.original
        : { start: oldStart, end: oldEnd, resource: oldResource };

    setTimelineEditDraft({
      bookingId,
      entryId: eventId,
      original: baseOriginal,
      draft: { start: newStart, end: newEnd, resource: newResource },
      isInformationBooking: !!args.event.isInformationBooking,
    });

    setEventsData((prev) =>
      (prev || []).map((evt) =>
        evt.id === eventId
          ? {
            ...evt,
            start: newStart,
            end: newEnd,
            resource: newResource,
          }
          : evt,
      ),
    );
  }, [setEventsData, timelineEditDraft]);

  const handleHiddenOverlapForNewEvent = useCallback(
    (normalizedEvent, eventId, eventRow, currentPendingReservations) => {
      const isBookingAdmin = user?.roles?.some((role) => role === "BookingAdmin");
      if (isBookingAdmin && !isEditMode && !isDuplicate) {
        const newPendingReservations = [...currentPendingReservations, eventRow];
        updatePendingReservation(newPendingReservations);
        setEventsData((prev) => [...prev, eventRow]);
        updateDatePicker(newPendingReservations);
        return;
      }
      hasHiddenOverlap(normalizedEvent)
        .then((result) => {
          if (result.hasError) {
            snackbarService.show({
              message: "Unexpected Server Error",
              type: "error",
            });
            setLocalFetchError(true);
            return;
          }

          if (result.hasOverlap) {
            toast({
              message: "Overlap not allowed - hidden events detected",
            });

            const filteredReservations = currentPendingReservations.filter(
              (item) => item.id !== eventId,
            );
            updatePendingReservation(filteredReservations);
            setEventsData((prev) =>
              prev.filter((item) => item.id !== eventId),
            );
            updateDatePicker(filteredReservations);
            return;
          }

          const newPendingReservations = [...currentPendingReservations, eventRow];
          updatePendingReservation(newPendingReservations);
          setEventsData((prev) => [...prev, eventRow]);
          updateDatePicker(newPendingReservations);
        })
        .catch((error) => {
          console.error("Error checking hidden overlap:", error);
          snackbarService.show({
            message: "Unexpected Server Error",
            type: "error",
          });
          setLocalFetchError(true);
        });
    },
    [
      hasHiddenOverlap,
      updatePendingReservation,
      setEventsData,
      updateDatePicker,
      user,
      isEditMode,
      isDuplicate,
    ],
  );

  /**
   * Event triggered when a timeline event is created
   *
   * @param {any} args - Args of created event
   * @param {inst} args - Instanse of the event calendar
   * @returns - False if validation fails
   */
  const onEventCreate = useCallback(
    (args, inst) => {
      if (isTvMode) return;

      const normalizedEvent = normalizeCreatedEvent(args.event);

      const validationError = validateCreatedEvent(args, inst, normalizedEvent);
      if (validationError) {
        handleCreateValidationError(validationError);
        return false;
      }

      const eventId = uniqueId("available_");
      const eventRow = buildAvailableEventRow(normalizedEvent, eventId);

      if (isTimelineView) {
        handleHiddenOverlapForNewEvent(
          normalizedEvent,
          eventId,
          eventRow,
          pendingReservations,
        );
      } else {
        addPendingEventRow(eventRow);
      }

      return false;
    },
    // eslint-disable-next-line
    [
      addPendingEventRow,
      pendingReservations,
      isTimelineView,
      handleCreateValidationError,
      normalizeCreatedEvent,
      handleHiddenOverlapForNewEvent,
      validateCreatedEvent,
    ],
  );

  /**
   * Event triggered when a timeline event is updated
   *
   * @param {any} args - Args of created event
   * @param {inst} args - Instanse of the event calendar
   * @returns - False if validation fails
   */
  const onEventUpdate = useCallback(
    (args, inst) => {
      const commonUpdateError = getCommonEventUpdateError(args, inst);
      if (commonUpdateError) {
        if (commonUpdateError.type === "toast") {
          toast({ message: commonUpdateError.message });
        }
        return false;
      }

      const nowUtc = moment.tz(siteTimezone).utc().format();

      const isExistingBookingEdit = isExistingTimelineBookingEdit({
        args,
        isEditMode,
        isTimelineView,
      });

      if (!isExistingBookingEdit) {
        const standardUpdateError = getStandardEventUpdateError(args);
        if (handleStandardEventUpdateError(standardUpdateError)) {
          return false;
        }

        const tempReservation = buildUpdatedPendingReservations(
          pendingReservations,
          {
            ...args.event,
            start: changeDateToUtcString(args.event.start),
            end: changeDateToUtcString(args.event.end),
          },
        );

        updateDatePicker(tempReservation);
        updatePendingReservation([...tempReservation]);
        return;
      }

      const existingBookingEditError = getExistingBookingEditError(args, nowUtc);
      if (existingBookingEditError) {
        toast({ message: existingBookingEditError });
        return false;
      }

      applyExistingBookingDraft(args);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      applyExistingBookingDraft,
      getCommonEventUpdateError,
      getExistingBookingEditError,
      getStandardEventUpdateError,
      handleStandardEventUpdateError,
      isEditMode,
      isTimelineView,
      pendingReservations,
      siteTimezone,
      updatePendingReservation,
      updateDatePicker,
    ],
  );

  /**
   * Event triggered when a timeline event is deleted
   *
   * @param {any} args - Args of created event
   * @param {inst} args - Instanse of the event calendar
   * @returns - False if validation fails
   */
  const onEventDeleted = useCallback(
    (args) => {
      if (isTimelineView) {
        return false;
      }

      const tempReservation = pendingReservations.filter(
        (item) => item.id !== args.event.id,
      );
      // Always update date picker to clear it when no reservations remain
      updateDatePicker(tempReservation);
      updatePendingReservation(tempReservation);
      setEventsData((prev) => {
        return prev.filter((item) => item.id !== args.event.id);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingReservations],
  );

  /**
   * Event triggered when a user manually delete event
   *
   * @param {id} args - Args of created event
   * @returns - False if validation fails
   */
  const eventSlotDelete = useCallback(
    (id) => {
      onEventDeleted({ event: { id: id } });
      setEventsData((prev) => prev.filter((x) => x.id !== id));
      const tempPendingReservations = pendingReservations.filter(
        (item) => item.id !== id,
      );
      updatePendingReservation(tempPendingReservations);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingReservations],
  );

  const handleTimelineEditCancel = useCallback(() => {
    if (!timelineEditDraft) return;
    const { entryId, original } = timelineEditDraft;
    setEventsData((prev) =>
      (prev || []).map((evt) =>
        evt.id === entryId
          ? {
            ...evt,
            start: original.start,
            end: original.end,
            resource: original.resource,
          }
          : evt,
      ),
    );
    setTimelineEditDraft(null);
  }, [timelineEditDraft, setEventsData]);


  const handleTimelineEditUpdate = useCallback(async () => {
    if (!timelineEditDraft || !onTimelineUpdateBooking) return;
    const editedEntry = (eventData || []).find(
      (evt) => evt.id === timelineEditDraft.entryId,
    );
    if (!editedEntry) {
      setTimelineEditDraft(null);
      return;
    }
    setIsUpdatingBooking(true);
    try {
      const result = await onTimelineUpdateBooking({
        bookingId: timelineEditDraft.bookingId,
        entryId: timelineEditDraft.entryId,
        original: timelineEditDraft.original,
        draft: timelineEditDraft.draft,
        editedEntry,
      });
      // If parent reroutes to step 3 or completes successfully, exit edit mode.
      // Parent can return { rerouted: true } to keep things explicit.
      if (result === false) {
        // Parent reported failure; keep edit mode active.
        return;
      }
      setTimelineEditDraft(null);
    } catch (error) {
      // On failure, keep edit state so user can retry or cancel.
      console.error("Timeline update failed", error);
    } finally {
      setIsUpdatingBooking(false);
    }
  }, [timelineEditDraft, onTimelineUpdateBooking, eventData]);

  const handleCreateFlowCancel = useCallback(() => {
    if (onCancelCreateFlow) {
      onCancelCreateFlow();
      return;
    }
    const pendingIds = new Set((pendingReservations || []).map((r) => r.id));
    setEventsData((prev) =>
      (prev || []).filter((evt) => !pendingIds.has(evt.id)),
    );
    updatePendingReservation([]);
    updateDatePicker([]);
  }, [onCancelCreateFlow, pendingReservations, setEventsData, updatePendingReservation, updateDatePicker]);

  const handlerOnDelete = (deleteResourceId) => {
    handleOnClose();
    removeEquipmentAndReservation(deleteResourceId, pendingReservations);
  };
  const handleActiveDateChange = (ev) => {
    if (ev.active === "start" && isOngoing) {
      setTimeout(() => {
        rangeInst.setActiveDate("end");
      });
    }
  };
  const updateLoaderinDatePicker = (event) => {
    const firstDay = event.firstDay;
    const lastDay = event.lastDay;
    const dateList = eachDayOfInterval({
      start: firstDay,
      end: lastDay,
    });
    const tempLabelsData = dateList.map((item) => {
      return {
        date: `${moment(item).format("YYYY-MM-DD")}T00:00`,
        text: "...",
        color: "white",
      };
    });
    setLabelsData(tempLabelsData);
    setColorsData([]);
  };
  const getAvailability = async (ev, requestId) => {
    let startDate = moment.tz(siteTimezone).isAfter(moment(ev.firstDay))
      ? moment.tz(siteTimezone).startOf("day")
      : moment(ev.firstDay).startOf("day");
    let endDate = `${moment(startDate.format("YYYY-MM-DD")).endOf("month").format("YYYY-MM-DD")}T23:59:59Z`;

    let originalStartDate = null;
    let originalEndDate = null;
    let originalStartDateString = null;
    let originalEndDateString = null;
    let isDateSame = false;
    if (isEditMode) {
      originalStartDateString = moment(editBookingData?.dateFrom)
        .tz(siteTimezone)
        .format("YYYY-MM-DD");
      originalEndDateString = moment(editBookingData?.dateTo)
        .tz(siteTimezone)
        .format("YYYY-MM-DD");
      originalStartDate = editBookingData?.dateFrom;
      originalEndDate = editBookingData?.dateTo;
      isDateSame = isSameDayBooking(
        originalStartDate,
        originalEndDate,
        siteTimezone,
      );
    }
    const months = [];
    let count = 0;
    while (count < 3) {
      months.push({
        start: `${startDate.format("YYYY-MM-DD")}T00:00`,
        end: endDate,
      });
      startDate.add(1, "month").startOf("month");
      endDate = `${moment(startDate.format("YYYY-MM-DD")).endOf("month").format("YYYY-MM-DD")}T23:59:59Z`;
      count++;
    }

    let tempResult = [];
    await Promise.all(
      months.map(async (item) => {
        const { items } = await getAllData({
          client,
          query: FIND_BOOKABLE_INSTRUMENTS,
          drillData: true,
          fetchPolicy: "no-cache",
          variables: {
            siteTimezone: siteTimezone,
            startDate: item.start,
            endDate: item.end,
            inventoryIds: availabilityInventoryIds,
          },
          dataPath: ["data", "findBookableInstruments"],
        });
        tempResult.push(...items);
      }),
    );
    let tempResultColorsData = [];
    let tempResultLablesData = [];
    let tempDisableDateList = [];

    tempResult.forEach((item) => {
      const normalizedAvailability = normalizeAvailabilityValue(item.availability);
      const normalizedAvailabilityUpper = normalizedAvailability.toUpperCase();
      const isFullyAvailable = isFullyAvailableDate(normalizedAvailabilityUpper);

      if (normalizedAvailabilityUpper === "NONE") {
        // for disabling date
        const localDate = cloneDeep(item.date);
        if (isOngoing) {
          tempDisableDateList.push({
            start: moment(originalStartDate)
              .tz(siteTimezone)
              .add(1, "minutes")
              .utc()
              .format(),
            end: moment.tz(siteTimezone).startOf("hour").utc().format(),
          });
        }

        if (isEditMode) {
          if (localDate === originalStartDateString) {
            tempResultColorsData.push({
              date: localDate,
            });
            if (!isDateSame) {
              tempDisableDateList.push({
                start: moment(originalStartDate)
                  .tz(siteTimezone)
                  .add(1, "hours")
                  .utc()
                  .format(),
                end: moment(originalStartDate)
                  .tz(siteTimezone)
                  .endOf("day")
                  .utc()
                  .format(),
              });
            }
          } else if (localDate === originalEndDateString && !isDateSame) {
            tempResultColorsData.push({
              date: localDate,
            });
            tempDisableDateList.push({
              start: moment(originalEndDate)
                .tz(siteTimezone)
                .add(1, "hours")
                .utc()
                .format(),
              end: moment(originalEndDate)
                .tz(siteTimezone)
                .endOf("day")
                .utc()
                .format(),
            });
          } else {
            tempResultColorsData.push({
              date: localDate,
            });
            tempDisableDateList.push(localDate);
          }
        } else {
          tempResultColorsData.push({
            date: item.date,
          });
          tempDisableDateList.push(localDate);
        }
      } else if (isFullyAvailable) {
        tempResultColorsData.push({
          date: item.date,
          textColor: DATEPICKER_FULL_AVAILABILITY_COLORS.textColor,
        });
      }
      const labelText = getAvailabilityLabelText({
        availability: item.availability,
        selectedCount: availabilityInventoryIds.length,
      });

      if (labelText) {
        tempResultLablesData.push({
          date: item.date,
          text: labelText,
          color: "white",
          textColor: isFullyAvailable
            ? DATEPICKER_FULL_AVAILABILITY_COLORS.textColor
            : DATEPICKER_PARTIAL_AVAILABILITY_TEXT_COLOR,
          cellCssClass: "custom-label-text",
        });
      }
    });

    if (requestId !== datepickerAvailabilityRequestRef.current) {
      return;
    }

    setColorsData(tempResultColorsData);
    setLabelsData(tempResultLablesData);
    setDisableDateList(tempDisableDateList);
  };
  const onPageLoadingInDatePicker = useCallback(
    async (event) => {
      const requestId = datepickerAvailabilityRequestRef.current + 1;
      datepickerAvailabilityRequestRef.current = requestId;
      updateLoaderinDatePicker(event);
      await getAvailability(event, requestId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availabilityInventoryIds, selectedStartDate],
  );

  useEffect(() => {
    if (!calendarInstance) return;

    let resizeTimeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        changeView(view);

        if (calendarInstance.refresh) {
          calendarInstance.refresh();
        }
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [calendarInstance, view, isTvMode, setupColumnWidth, changeView]);

  // Update events with recurrance rule
  const eventDataWithRecurrance = useMemo(() => {
    const merged = [...eventData, ...(!isTvMode ? transformGoogleEvents(googleEvents) : [])].map(data =>
      data.active || data.googleCalendarId ? data : {
        ...data,
        recurring: getTrimmedRRuleString(displayTimeZone)
      }
    );


    if (!isTimelineView || isEditMode || isTvMode || isRecurringBooking) {
      return merged;
    }
    const nowUtc = moment.tz(siteTimezone).utc().format();
    const currentHalfHourSlotUtc = getCurrentHalfHourSlotUtc(siteTimezone);
    return merged.map((evt) => {
      if (!evt) return evt;
      if (evt.googleCalendarId) return evt;
      if (typeof evt.id === "string" && evt.id.startsWith("available_")) return evt;
      if (!evt.bookingId) return evt;

      const evtStart = changeDateToUtcString(evt.start);
      const evtEnd = changeDateToUtcString(evt.end);
      const existingCssClass = typeof evt.cssClass === "string" ? evt.cssClass.trim() : "";
      const cssClassTokens = existingCssClass ? existingCssClass.split(/\s+/).filter(Boolean) : [];
      const isStartLocked = evtStart < currentHalfHourSlotUtc;

      if (isStartLocked) {
        cssClassTokens.push("timeline-start-locked");
      }

      const cssClass = cssClassTokens.length ? Array.from(new Set(cssClassTokens)).join(" ") : undefined;

      if (evtEnd <= nowUtc) {
        return { ...evt, editable: false, resize: false, cssClass };
      }

      return { ...evt, editable: true, resize: true, cssClass };
    });
  }, [
    eventData,
    getTrimmedRRuleString,
    googleEvents,
    isTvMode,
    transformGoogleEvents,
    displayTimeZone,
    isTimelineView,
    isEditMode,
    isRecurringBooking,
    siteTimezone,
  ]);

  useEffect(() => {
    if (eventDataWithRecurrance?.length) {
      setEventDataContext(eventDataWithRecurrance)
    }
  }, [eventDataWithRecurrance, setEventDataContext]);

  // Combine Google Calendar resources with equipment resources
  const resourcesCombined = useMemo(() => {
    return [
      ...(!isTvMode ? googleCalendarResources : []),
      ...groupByControlResource,
      ...sortedGroupedResources,
    ].map(res => ({
      ...res,
      eventOverlap: true,
    }));
  }, [
    googleCalendarResources,
    groupByControlResource,
    isTvMode,
    sortedGroupedResources,
  ]);

  useEffect(() => {
    setRecoursesContext(resourcesCombined)
  }, [setRecoursesContext, resourcesCombined]);

  const datepickerMinValue = useMemo(() => getDatepickerMinValue({
    isEditMode,
    isOngoing,
    selectedStartDate,
    siteTimezone,
  }), [isEditMode, isOngoing, selectedStartDate, siteTimezone]);

  const calendarSelectedDate = useMemo(() => getCalendarSelectedDate({
    timelineSelectedDate,
    siteTimezone,
    isTvMode,
  }), [timelineSelectedDate, siteTimezone, isTvMode]);

  const calendarRefDate = useMemo(() => getCalendarRefDate({
    timelineSelectedDate,
    siteTimezone,
    isTvMode,
    view,
  }), [timelineSelectedDate, siteTimezone, isTvMode, view]);

  const handleDatepickerTempChange = useCallback((event) => {
    const value = event?.value || [];
    if (JSON.stringify(value) === JSON.stringify(temporarySelectedDates)) {
      return;
    }

    isDatepickerCancelledRef.current = false;
    setTemporaryStartDate(value[0]);
    setTemporaryEndDate(value[1]);
    debouncedSetTempDates(value);
  }, [debouncedSetTempDates, setTemporaryEndDate, setTemporaryStartDate, temporarySelectedDates]);

  const handleDatepickerClose = useCallback((param) => {
    if (isDatepickerCancelledRef.current) {
      isDatepickerCancelledRef.current = false;
      restoreOriginalDatepickerSelection({
        debouncedSetTempDates,
        originalSelectedEndDate,
        originalSelectedStartDate,
        setTemporaryEndDate,
        setTemporaryStartDate,
      });
      return;
    }

    if (!hasDatepickerRangeChanged({
      selectedEndDate,
      selectedStartDate,
      value: param?.value,
    })) {
      syncSelectedDatesFromDatepicker({
        setSelectedEndDate,
        setSelectedStartDate,
        value: param?.value,
      });
      return;
    }

    if (!isEditMode) {
      handleDateChange(param, true);
      return;
    }

    if (isDatepickerEditRangeChangeInvalid({
      isOngoing,
      nextValue: param?.value,
      selectedEndDate,
      selectedStartDate,
      siteTimezone,
    })) {
      return false;
    }

    if (isOngoing || isDuplicate) {
      handleDateChange(param, true);
      return;
    }

    dispatchPopupWithLoader({
      type: "eventBooking",
      payload: {
        onApprove: () => handleDateChange(param),
        title: "Experiment range changed",
        content: `You have selected a new experiment range.
                                  Please be aware that your old equipment timeslots are removed.`,
        onclose: handleOnClose,
      },
    });
  }, [
    debouncedSetTempDates,
    dispatchPopupWithLoader,
    handleDateChange,
    handleOnClose,
    isDuplicate,
    isEditMode,
    isOngoing,
    originalSelectedEndDate,
    originalSelectedStartDate,
    selectedEndDate,
    selectedStartDate,
    setSelectedEndDate,
    setSelectedStartDate,
    setTemporaryEndDate,
    setTemporaryStartDate,
    siteTimezone,
  ]);

  const handleDatepickerCancel = useCallback(() => {
    isDatepickerCancelledRef.current = true;
    dpCloseReasonRef.current = "cancel";
    if (!timelineSelectedDate) {
      setTimelineSelectedDate(moment().tz(siteTimezone).format());
    }
  }, [siteTimezone, timelineSelectedDate]);

  const hasBookableEquipmentFlag = hasBookableEquipment();
  const createBookingDisabled = !canCreateBooking({
    hasBookableEquipment: hasBookableEquipmentFlag,
    hasServerError,
    pendingReservationsLength: pendingReservations.length,
  });
  const createBookingTitle = getCreateBookingTitle({
    hasBookableEquipment: hasBookableEquipmentFlag,
    hasServerError,
    pendingReservationsLength: pendingReservations.length,
  });
  const calendarCssClass = useMemo(() => getCalendarCssClass({
    hasBookableEquipment: hasBookableEquipmentFlag,
    isTimelineView,
    isTvMode,
    view,
  }), [hasBookableEquipmentFlag, isTimelineView, isTvMode, view]);
  const clickToCreateValue = useMemo(() => getClickToCreateValue({
    isDatepickerOpen,
    isRecurringBooking,
    isTvMode,
  }), [isDatepickerOpen, isRecurringBooking, isTvMode]);
  const resolvedClickToCreate = useMemo(() => resolveClickToCreateProp(clickToCreateValue), [clickToCreateValue]);

  const toggleLegendVisibility = useCallback((event) => {
    event.preventDefault();
    setIsLegendVisible((prev) => !prev);
  }, []);

  const handleCreateBookingClick = useCallback(() => {
    const unauthorizedEquipment = getUnauthorizedEquipmentDetails({
      getEquipmentBookingStatus,
      pendingReservations,
      resourceData,
    });

    if (unauthorizedEquipment.length > 0) {
      toast({
        message: getUnauthorizedEquipmentMessage(unauthorizedEquipment),
      });
      return;
    }

    const hasOverlapWithRegular = detectOverlapWithRegularBookings(
      pendingReservations,
      eventData,
    );
    // BE rejects duplicate Run/Action bookings on the same timeframe, so we
    // also force Information when two pending slots overlap each other.
    const hasOverlapWithinPending = detectOverlapWithinPending(pendingReservations);
    dispatch(setForceInformationOnly(hasOverlapWithRegular || hasOverlapWithinPending));

    if (handleCreateBookingFromEquipmentView) {
      handleCreateBookingFromEquipmentView();
    }
  }, [
    getEquipmentBookingStatus,
    handleCreateBookingFromEquipmentView,
    pendingReservations,
    resourceData,
    eventData,
    dispatch,
  ]);

  const renderMyHeaderRef = useRef(renderMyHeader);
  renderMyHeaderRef.current = renderMyHeader;
  const stableRenderMyHeader = useCallback(() => renderMyHeaderRef.current(), []);

  const renderCustomHourRef = useRef(renderCustomHour);
  renderCustomHourRef.current = renderCustomHour;
  const stableRenderCustomHour = useCallback((args) => renderCustomHourRef.current(args), []);

  const renderCustomScheduleEventRef = useRef(renderCustomScheduleEvent);
  renderCustomScheduleEventRef.current = renderCustomScheduleEvent;
  const stableRenderCustomScheduleEvent = useCallback((data) => renderCustomScheduleEventRef.current(data), []);

  const customResourceRef = useRef(customResource);
  customResourceRef.current = customResource;
  const stableCustomResource = useCallback((resource) => customResourceRef.current(resource), []);

  const customResourceHeaderRef = useRef(customResourceHeader);
  customResourceHeaderRef.current = customResourceHeader;
  const stableCustomResourceHeader = useCallback(() => customResourceHeaderRef.current(), []);

  const handlePageLoaded = useCallback((_args, inst) => {
    setupColumnWidth(inst, view, isTvMode);
  }, [setupColumnWidth, view, isTvMode]);

  return (
    <>
      {visibleDatePicker && (
        <div
          className="customeventcalendar_datepickerInputStyle"
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <Datepicker
            id="datepicker-from-my-booking"
            controls={["calendar", "timegrid"]}
            ref={setRangeInst}
            labels={labelsData}
            colors={colorsData}
            onPageLoading={onPageLoadingInDatePicker}
            onInit={(event, inst) => {
              // check cond if it needs to be opened
              if (!(selectedStartDate && selectedEndDate)) {
                inst.open();
              }
            }}
            value={[temporaryStartDate, temporaryEndDate]}
            closeOnOverlayClick={false}
            inputStyle="outline"
            select="range"
            display="anchored"
            anchorAlign={alignDatePicker(viewportWidth, isEditMode, isDuplicate)}
            inRangeInvalid={isEditMode}
            rangeEndInvalid={isEditMode}
            headerText="Experiment date(range)"
            stepMinute={60}
            touchUi={"auto"}
            min={datepickerMinValue}
            minRange={3600000}
            dateFormat="D-MMM-YYYY"
            startIcon={<OwcIcon name="date" type="outlined" />}
            onCellClick={handleOnCellClick}
            onTempChange={handleDatepickerTempChange}
            onClose={handleDatepickerClose}
            onCancel={handleDatepickerCancel}
            timezonePlugin={momentTimezone}
            dataTimezone={dataTimeZone}
            displayTimezone={displayTimeZone}
            buttons={['cancel', 'set']}
            onActiveDateChange={handleActiveDateChange}
            invalid={disableDateList}
          >
            {(!isEditMode || isDuplicate) && <RecurringBookingPicker />}
          </Datepicker>
        </div>
      )}
      <Eventcalendar
        theme="ios"
        height={"100%"}
        view={calView}
        resources={resourcesCombined}
        data={eventDataWithRecurrance}
        scroll={isTvMode ? "06:00" : undefined}
        renderHeader={stableRenderMyHeader}
        renderResource={stableCustomResource}
        renderResourceHeader={stableCustomResourceHeader}
        renderHour={stableRenderCustomHour}
        renderScheduleEvent={stableRenderCustomScheduleEvent}
        ref={calendarRef}
        onInit={handleCalendarInit}
        onPageLoaded={handlePageLoaded}
        cssClass={calendarCssClass}
        clickToCreate={resolvedClickToCreate}
        dragToCreate={!isRecurringBooking && !isDatepickerOpen && !isTvMode}
        dragToMove={!isRecurringBooking && !isDatepickerOpen}
        dragToResize={!isRecurringBooking && !isDatepickerOpen}
        dragTimeStep={30}
        selectedDate={calendarSelectedDate}
        refDate={calendarRefDate}
        onSelectedDateChange={onSelectedDateChange}
        onPageLoading={onPageLoading}
        onVirtualLoading={onTimelineVirtualLoading}
        onEventCreate={onEventCreate}
        onEventUpdate={onEventUpdate}
        onEventDeleted={onEventDeleted}
        dataTimezone={dataTimeZone}
        displayTimezone={displayTimeZone}
        timezonePlugin={momentTimezone}
        eventOrder={informationFirstEventOrder}
        eventOverlap={true}
      />

      {Boolean(conflicts.length) && (
        <ConflictPanel conflicts={conflicts} />
      )}
      <TimelineFooter
        createBookingDisabled={createBookingDisabled}
        createBookingTitle={createBookingTitle}
        handleCreateBookingClick={handleCreateBookingClick}
        handleCreateFlowCancel={handleCreateFlowCancel}
        handleTimelineEditCancel={handleTimelineEditCancel}
        handleTimelineEditUpdate={handleTimelineEditUpdate}
        hasServerError={hasServerError}
        isInformationEdit={!!timelineEditDraft?.isInformationBooking}
        isLegendVisible={isLegendVisible}
        isTimelineEditMode={isTimelineEditMode}
        isTimelineView={isTimelineView}
        isTvMode={isTvMode}
        isUpdatingBooking={isUpdatingBooking}
        pendingReservationsLength={pendingReservations.length}
        toggleLegendVisibility={toggleLegendVisibility}
      />
    </>
  );
};

export {
  generateUserColor,
  getAvailabilityLabelText,
  isFullyAvailableDate,
  normalizeAvailabilityValue,
  resolveAvailabilityInventoryIds,
};

export default withApollo(CustomEventCalendar);
